import { useState, useEffect } from 'react'
import { Navigate, useParams, useNavigate } from 'react-router-dom'
import * as api from '../lib/api'
import { DATE_FORMAT_LABEL, formatDate } from '../lib/dateFormat'
import {
  configuredRatingLabel,
  getConfiguredFactorRatingOptions,
  getConfiguredObjectiveRow,
  getConfiguredOverallRatingField,
  getConfiguredOverallRatingOptions,
  getConfiguredOverallRatingValue,
  hasObjectiveEntry as hasConfiguredObjectiveEntry,
  hasObjectiveText as hasConfiguredObjectiveText,
  isConfiguredFormSectionEnabled,
  objectivePriorityTotal,
  useFormStructure,
  usesConfiguredAppraisalLayout,
  ratingOptionText
} from './useFormStructure'
import { Card, Input, Select, Button, Spin, Alert, Typography, Space, Row, Col, message, Modal, Tabs, Collapse } from 'antd'
import Popconfirm from '../components/Popconfirm'
import { businessUnitThemeVars, getBusinessUnitTheme } from './businessUnitTheme'
import { getConfiguredRatingDisplay, getConfiguredRatingFullLabel, getConfiguredRatingOptions, getEnabledRatingOptions, getRatingGuideData, getRatingOptionLabel, getRatingValueDisplay, usesRatingGuideTable } from './ratingScale'
import RatingGuideTable from './RatingGuideTable'
import BackButton from './BackButton'
import AppraisalConversationSidebar from './AppraisalConversationSidebar'
import ManagerFeedback from './ManagerFeedback'
import { DeleteOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { getPrcSuccessIndicatorValue, isPrcAppraisal, prcEmployeeInformationFields, prcEmployeeInformationTitle, prcEvaluationLevel, prcIdpFields, prcObjectiveConfig, prcObjectiveScoreLabel, prcObjectiveScoreMax } from './prcFlow'
import PrcRatingGuideDescription from './PrcRatingGuideDescription'
import { CultureValuesCheckboxes, NextYearObjectivesCard, OverallPerformanceRatingCheckboxes, checkboxRatingGuideSource, hasRequiredOverallCheckboxRating, isMilAsiaCheckboxFlow, setCheckboxFlowData } from './milAsiaCheckboxFlow'
import PerformanceFactorsCard from './PerformanceFactorsCard'
import { MilwaukeeAsiaEnglishSections, isConfiguredRegionalForm, renderBoldMarkup, validateMilwaukeeAsiaEnglishManagerSections } from './milwaukeeAsiaEnglish'
import { unresolvedReturnForStatus } from './ReturnHistory'
import { approverName, personLabel } from './HRDashboard'
import { competencySuggestedRating, objectiveSuggestedRating, overallAppraisalAverage, overallSuggestedRating, weightedObjectiveAverage } from './objectiveScores'
import { requiresVietnamMilwaukeeJustification, vietnamMilwaukeeJustificationMinLength } from './vietnamMilwaukeeJustification'
import SuggestedRatingBand from './SuggestedRatingBand'
import CalculatedRatingBand from './CalculatedRatingBand'

const { Title, Text } = Typography
const { TextArea } = Input
const { Option } = Select
const SubmitFeedbackIcon = () => (
  <svg aria-hidden='true' width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <path d='M2 2L18 10L2 18V2ZM3.6 12L10 10L3.6 8V4.58885L14.4223 10L3.6 15.4111V12Z' fillRule='evenodd' clipRule='evenodd' fill='#0080C1' />
  </svg>
)
const getRatingCategory = (value) => {
  const rating = parseFloat(value)
  if (!rating) return null
  if (rating <= 1.5) return { code: 'NI', rating: 'Needs Improvement' }
  if (rating <= 1.75) return { code: 'ME-', rating: 'Meets Expectations-' }
  if (rating <= 2.25) return { code: 'ME', rating: 'Meets Expectations' }
  if (rating <= 2.5) return { code: 'ME+', rating: 'Meets Expectations+' }
  if (rating <= 2.75) return { code: 'EE-', rating: 'Exceeds Expectations-' }
  if (rating <= 3.25) return { code: 'EE', rating: 'Exceeds Expectations' }
  if (rating <= 3.5) return { code: 'EE+', rating: 'Exceeds Expectations+' }
  return { code: 'E', rating: 'Excels' }
}

const getRatingCategoryDisplay = (value) => {
  const category = getRatingCategory(value)
  return category ? `${category.code} - ${category.rating}` : null
}

const getScoreDisplay = (value) => {
  if (value === null || value === undefined || value === '') return null
  return `${value}`
}

const isWholeNumberInput = value => value === '' || /^\d+$/.test(value)

const RatingGuideDescription = ({ source }) => {
  const { conversionGuide, descriptions } = getRatingGuideData(source)
  if (usesRatingGuideTable(source)) return <RatingGuideTable source={source} />

  return (
    <div style={{ fontSize: 12 }}>
      <Text strong>Performance Rating Conversion</Text>
      <Row gutter={[16, 8]} style={{ marginTop: 8 }}>
        {conversionGuide.map(item => (
          <Col span={6} key={item.code}>
            <Text strong>{item.code}:</Text> {item.rating} ({item.range})
          </Col>
        ))}
      </Row>

      <div style={{ marginTop: 16 }}>
        <Text strong>Performance Rating Description</Text>
        <div style={{ marginTop: 8 }}>
          {descriptions.map(item => (
            <div key={item.code} style={{ marginBottom: 8 }}>
              <Text strong>{item.code} ({item.score}) - {item.rating}:</Text> {item.description}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// Form structure with categories and questions
const FORM_STRUCTURE = [
  {
    id: 'quality_of_work',
    title: 'Category 1: Quality of Work',
    questions: [
      { id: 'q1', text: 'Pursues quality and achieves excellence in all tasks and goals.' },
      { id: 'q2', text: 'Takes initiative and produces work consistent with defined organizational standards for the position.' },
      { id: 'q3', text: 'Diligently attends to detail; performs tasks with accuracy, completeness and high quality.' },
      { id: 'q4', text: 'Addresses problems and overcomes obstacles successfully to timely completion of tasks.' },
      { id: 'q5', text: 'Demonstrates self-reliance and accomplishes work objectives with minimal supervision.' }
    ]
  },
  {
    id: 'work_attitude',
    title: 'Category 2: Work Attitude',
    questions: [
      { id: 'q1', text: 'Demonstrates the attitude of ownership, and creativity to accomplish tasks.' },
      { id: 'q2', text: 'Takes ownership for the success and quality of work produced.' },
      { id: 'q3', text: 'Accepts responsibilities for own behaviors, faces up to problems, acknowledges and corrects mistakes.' },
      { id: 'q4', text: 'Embraces diverse perspectives to promote or nurture innovation.' },
      { id: 'q5', text: 'Generates new ideas, solutions and creative approaches.' }
    ]
  },
  {
    id: 'knowledge',
    title: 'Category 3: Knowledge',
    questions: [
      { id: 'q1', text: 'Possesses requisite knowledge and skills for assigned roles and responsibilities. Willing to acquire new skills for self development and advancement.' },
      { id: 'q2', text: 'Demonstrates relevant job knowledge and essential skills in performing duties and functions of the position.' },
      { id: 'q3', text: 'Displays a high understanding of all work practices, policies, and procedures.' },
      { id: 'q4', text: 'Actively participates in appropriate learning and training activities that expand job knowledge and effectiveness.' },
      { id: 'q5', text: 'Shares information and expertise to assist others to accomplish work assignments.' }
    ]
  },
  {
    id: 'communication',
    title: 'Category 4: Communication',
    questions: [
      { id: 'q1', text: 'Listens to others and communicates with clarity and effectiveness.' },
      { id: 'q2', text: 'Provides regular, consistent and meaningful information and ideas with others to make sure all appropriate individuals are informed.' },
      { id: 'q3', text: 'Communicates in a clear and concise manner using appropriate language, tone, and structure.' },
      { id: 'q4', text: 'Listens carefully to others, opens to other points of view, asks questions for clarification and ensures what others have said is understood.' },
      { id: 'q5', text: 'Gives and receives constructive feedback to foster effective working relationships.' }
    ]
  },
  {
    id: 'planning_organizing',
    title: 'Category 5: Planning & Organizing',
    questions: [
      { id: 'q1', text: 'Identifies and priorities tasks, formulates and executes plans to achieve objectives.' },
      { id: 'q2', text: 'Manages multiple duties and tasks effectively and efficiently.' },
      { id: 'q3', text: 'Identifies and organizes the varied resources needed to achieve goals and prioritizes key action steps.' },
      { id: 'q4', text: 'Prepares clear plans with strategic focus, specific milestones and deadlines prior to initiative project work.' },
      { id: 'q5', text: 'Develops contingency plans for potential problems or drastic changes.' }
    ]
  },
  {
    id: 'interaction_with_others',
    title: 'Category 6: Interaction with Others',
    questions: [
      { id: 'q1', text: 'Positively interacts with a diverse workforce.' },
      { id: 'q2', text: 'Works with team members and others in ways that strengthen optimal team interaction and results.' },
      { id: 'q3', text: 'Encourages and supports colleagues in expressing ideas and sharing information.' },
      { id: 'q4', text: 'Cultivates positive relationships among team members by actively contributing to team processes and deliverables, such as team meetings and assignments.' },
      { id: 'q5', text: 'Demonstrates understanding, courtesy, empathy and genuine concern in all interactions.' }
    ]
  }
]

const hasObjectiveText = objective => Boolean((objective?.objective || '').trim())
const hasObjectiveScore = objective => (
  objective?.self_score !== null &&
  objective?.self_score !== undefined &&
  objective?.self_score !== ''
) || (
    objective?.manager_score !== null &&
    objective?.manager_score !== undefined &&
    objective?.manager_score !== ''
  )

const hasObjectiveFieldValue = value => (
  value !== null &&
  value !== undefined &&
  (typeof value !== 'string' || value.trim() !== '')
)

const hasAnnualObjectiveContent = objective => (
  ['objective', 'success_indicators', 'self_score', 'self_comment', 'manager_score', 'manager_comment']
    .some(field => hasObjectiveFieldValue(objective?.[field])) ||
  Object.entries(objective || {}).some(([field, value]) => (
    field.startsWith('success_indicator_') && hasObjectiveFieldValue(value)
  ))
)

const isDecimalInput = value => value === '' || /^\d*(?:\.\d*)?$/.test(value)

const isScoreOutsideObjectiveRange = (value, maxScore = 4) => {
  if (value === null || value === undefined || value === '') return false
  const score = parseFloat(value)
  return Number.isNaN(score) || score < 1 || score > maxScore
}

const normalizeObjectiveScore = (value, maxScore = 4) => {
  if (value === null || value === undefined || value === '') return ''
  const score = parseFloat(value)
  if (Number.isNaN(score)) return ''
  if (score < 1) return 1
  if (score > maxScore) return maxScore
  return value
}

const redactManagerFeedback = value => {
  if (Array.isArray(value)) return value.map(redactManagerFeedback)
  if (!value || typeof value !== 'object') return value
  return Object.fromEntries(Object.entries(value).map(([key, child]) => [
    key,
    key.startsWith('manager_') ? (key.includes('comment') ? '' : null) : redactManagerFeedback(child)
  ]))
}

const redactCopiedManagerFeedback = (reviewValue, managerValue) => {
  if (Array.isArray(reviewValue)) {
    const managerItems = Array.isArray(managerValue) ? managerValue : []
    return reviewValue.map((child, index) => redactCopiedManagerFeedback(child, managerItems[index]))
  }
  if (!reviewValue || typeof reviewValue !== 'object') return reviewValue
  const managerFields = managerValue && typeof managerValue === 'object' ? managerValue : {}
  return Object.fromEntries(Object.entries(reviewValue).map(([key, child]) => {
    const managerChild = managerFields[key]
    if (key.startsWith('manager_') && child === managerChild) {
      return [key, key.includes('comment') ? '' : null]
    }
    return [key, redactCopiedManagerFeedback(child, managerChild)]
  }))
}

const mergeReviewFeedback = (baseValue, reviewValue) => {
  if (reviewValue === undefined) return baseValue
  if (Array.isArray(baseValue) && Array.isArray(reviewValue)) {
    return Array.from(
      { length: Math.max(baseValue.length, reviewValue.length) },
      (_, index) => mergeReviewFeedback(baseValue[index], reviewValue[index])
    )
  }
  if (
    baseValue && reviewValue &&
    typeof baseValue === 'object' && typeof reviewValue === 'object' &&
    !Array.isArray(baseValue) && !Array.isArray(reviewValue)
  ) {
    return Object.fromEntries(
      [...new Set([...Object.keys(baseValue), ...Object.keys(reviewValue)])]
        .map(key => [key, mergeReviewFeedback(baseValue[key], reviewValue[key])])
    )
  }
  return reviewValue
}

const functionalReviewFormData = (appraisalFormData, reviewFormData) => mergeReviewFeedback(
  redactManagerFeedback(appraisalFormData || {}),
  redactCopiedManagerFeedback(reviewFormData || {}, appraisalFormData || {})
)

export default function ManagerForm({ viewerApproverLevel = null }) {
  const { id, reviewId } = useParams()
  const navigate = useNavigate()
  const [appraisal, setAppraisal] = useState(null)
  const [currentUser, setCurrentUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [savingDraft, setSavingDraft] = useState(false)
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const [formData, setFormData] = useState({})
  const [managerOverallComment, setManagerOverallComment] = useState('')
  const [objectives, setObjectives] = useState([])
  const [nextYearObjectives, setNextYearObjectives] = useState([])
  const [returnDrawerOpen, setReturnDrawerOpen] = useState(false)
  const [returnComments, setReturnComments] = useState('')
  const [resubmissionRemarks, setResubmissionRemarks] = useState('')
  const [commentRecipient, setCommentRecipient] = useState('employee')
  const [returningToEmployee, setReturningToEmployee] = useState(false)
  const firstApproverName = approverName(appraisal, 1)
  const approverRouteSegment = { 2: 'head', 3: 'bu-head', 4: 'approver-4', 5: 'approver-5' }[viewerApproverLevel]
  const appraisalFormRoute = approverRouteSegment
    ? `/appraisals/${id}/${approverRouteSegment}`
    : `/appraisals/${id}/manager`
  const selectedFunctionalReview = reviewId
    ? (appraisal?.functional_reviews || []).find(review => String(review.id) === String(reviewId))
    : null
  const isFunctionalReview = Boolean(appraisal?.active_functional_review)
  const isViewingReviewerFeedback = Boolean(selectedFunctionalReview && !isFunctionalReview)
  const displayedFunctionalReview = appraisal?.active_functional_review || selectedFunctionalReview
  const feedbackAuthorName = displayedFunctionalReview
    ? (displayedFunctionalReview.reviewer_name || displayedFunctionalReview.reviewer_email || 'Indirect Manager')
    : firstApproverName
  const displayedManagerFeedbackAvailable = Boolean(displayedFunctionalReview) || Boolean(appraisal?.manager_submitted_at)
  const displayedManagerSubmittedAt = displayedFunctionalReview?.submitted_at || appraisal?.manager_submitted_at
  const employeeName = personLabel(appraisal?.employee_first_name, appraisal?.employee_last_name, appraisal?.employee_email, '', appraisal?.session_template)
  const nextApproverName = appraisal?.approver_2
    ? approverName(appraisal, 2)
    : appraisal?.approver_3
      ? approverName(appraisal, 3)
      : 'No further approval'
  const managerReturn = unresolvedReturnForStatus(
    appraisal?.return_history,
    'pending_manager'
  )
  const isReturnedForRevision = appraisal?.status === 'pending_manager' && Boolean(
    managerReturn || (!appraisal.return_history?.length && appraisal.return_comments)
  )

  useEffect(() => {
    setLoading(true)
    Promise.all([api.getAppraisal(id), api.getMe()]).then(([a, me]) => {
      const selectedReview = reviewId
        ? (a.functional_reviews || []).find(review => String(review.id) === String(reviewId))
        : null
      const functionalReview = a.active_functional_review || selectedReview
      const reviewFormData = functionalReview?.form_data || {}
      const mergedFormData = functionalReview
        ? functionalReviewFormData(a.form_data, reviewFormData)
        : (a.form_data || {})
      const submittedObjectives = mergedFormData.annual_objectives && mergedFormData.annual_objectives.length > 0
        ? mergedFormData.annual_objectives
        : (mergedFormData.objectives || [])
      setAppraisal(a)
      setCurrentUser(me)
      setFormData(mergedFormData)
      setManagerOverallComment(
        functionalReview
          ? (functionalReview.manager_overall_comment === a.manager_overall_comment
              ? ''
              : (functionalReview.manager_overall_comment || ''))
          : (a.manager_overall_comment || '')
      )
      setReturnComments(functionalReview ? '' : (a.manager_draft_return_comments || ''))
      setReturnDrawerOpen(Boolean(a.return_comments || a.return_history?.length))
      setCommentRecipient(!functionalReview && a.status === 'pending_manager' &&
        Boolean(
          unresolvedReturnForStatus(a.return_history, 'pending_manager') ||
          (!a.return_history?.length && a.return_comments)
        )
        ? 'approver'
        : 'employee')
      setObjectives(submittedObjectives.filter(objective => hasObjectiveText(objective) || hasObjectiveScore(objective)))
      setNextYearObjectives(
        mergedFormData.next_year_objectives && mergedFormData.next_year_objectives.length > 0
          ? mergedFormData.next_year_objectives
          : Array.from({ length: 3 }, () => ({ priority: '', objective: '' }))
      )
    }).finally(() => setLoading(false))
  }, [id, reviewId])
  const {
    formStructure,
    formConfig,
    loading: structureLoading,
    error: structureError
  } = useFormStructure(appraisal?.session_template || appraisal?.session_business_unit, FORM_STRUCTURE)
  const objectivesConfig = formConfig?.objectives || {}
  const showObjectiveAverageScore = objectivesConfig.show_average_score !== false
  const showCategoryAverageAsRating = formConfig?.competencies?.category_average_display === 'rating_name'
  const categoryAverageLabel = formConfig?.competencies?.category_average_label || 'Calculated Rating'
  const competencySummaryTitle = formConfig?.competencies?.summary_title || 'Competency Average Score'
  const competencySummarySubtitle = formConfig?.competencies?.summary_subtitle
  const singleCategoryComment = appraisal?.session_template === 'vietnam_milwaukee' && formConfig?.competencies?.single_category_comment === true
  const categoryCommentQuestionKey = formConfig?.competencies?.category_comment_question_key || 'q1'
  const categoryCommentLabel = formConfig?.competencies?.category_comment_label || 'Comment'
  const categoryCommentRequired = singleCategoryComment && formConfig?.competencies?.category_comment_required === true
  const overallPerformanceTitle = formConfig?.overall_performance?.summary_title || 'Overall Average Score'
  const competencyManualRating = formConfig?.competencies?.manual_rating || {}
  const overallManualRating = formConfig?.overall_performance?.manual_rating || {}
  const employeeCompetencyRatingField = competencyManualRating.employee_field || 'employee_competency_overall_rating'
  const managerCompetencyRatingField = competencyManualRating.manager_field || 'manager_competency_overall_rating'
  const employeeOverallRatingField = overallManualRating.employee_field || 'employee_overall_performance_rating'
  const managerOverallRatingField = overallManualRating.manager_field || 'manager_overall_performance_rating'
  const configuredManagerOverallRatingTitle = formConfig.appraisal_process?.manager_overall_rating_title ||
    `${formConfig.appraisal_process?.overall_rating_label || 'Overall Performance Rating'} - ${formConfig.appraisal_process?.manager_evaluation_label || 'Manager Rating'}`
  const isConfiguredManagerOverallRatingMissing = showValidationErrors &&
    usesConfiguredAppraisalLayout(formConfig) &&
    !getConfiguredOverallRatingValue(formData, formConfig)
  const isManagerOverallPerformanceRatingMissing = showValidationErrors &&
    overallManualRating.enabled &&
    overallManualRating.required &&
    !formData[managerOverallRatingField]
  const showCompetencySummarySubtext = formConfig?.competencies?.show_summary_subtext !== false
  const showOverallSummarySubtext = formConfig?.overall_performance?.show_summary_subtext !== false
  const objectivePriorityOptions = Array.isArray(objectivesConfig.priority_options) ? objectivesConfig.priority_options : []
  const usesPriorityOptions = objectivePriorityOptions.length > 0
  const allowedPriorityValues = new Set(objectivePriorityOptions.map(option => `${option.value}`))
  const isLegacyNumericPriority = value => value !== '' && value !== null && value !== undefined && !Number.isNaN(Number(value))
  useEffect(() => {
    if (
      appraisal &&
      !structureLoading &&
      usesConfiguredAppraisalLayout(formConfig) &&
      !isViewingReviewerFeedback &&
      !appraisal.form_data?.next_year_objectives?.length
    ) {
      setNextYearObjectives(Array.from({ length: 3 }, () => ({ ...getConfiguredObjectiveRow(formConfig) })))
    }
  }, [appraisal, formConfig, isViewingReviewerFeedback, structureLoading])
  const usesConfiguredRatings = Array.isArray(formConfig.rating_options) && formConfig.rating_options.length > 0
  const allowDecimalObjectiveScores = appraisal?.session_template === 'vietnam_tti' || objectivesConfig.allow_decimal_scores === true
  const usesConfiguredObjectiveRatings = usesConfiguredRatings && !allowDecimalObjectiveScores
  const getQuestionRatingOptions = (currentValue) => usesConfiguredRatings
    ? getConfiguredRatingOptions(formConfig, currentValue)
    : getEnabledRatingOptions(
      checkboxRatingGuideSource(appraisal, formConfig),
      currentValue === null || currentValue === undefined || currentValue === '' ? [] : [currentValue]
    )
  const getQuestionRatingLabel = option => usesConfiguredRatings ? option.label : getRatingOptionLabel(option)
  const isObjectiveScoreInvalid = value => {
    if (value === null || value === undefined || value === '') return false
    return usesConfiguredObjectiveRatings
      ? !getConfiguredRatingOptions(formConfig).some(option => option.value === Number(value))
      : isScoreOutsideObjectiveRange(value, prcObjectiveScoreMax(appraisal, formConfig))
  }

  const updateField = (categoryId, questionId, field, value) => {
    setFormData(prev => ({
      ...prev,
      [categoryId]: {
        ...prev[categoryId],
        [questionId]: {
          ...prev[categoryId]?.[questionId],
          [field]: value
        }
      }
    }))
  }

  const getFieldValue = (categoryId, questionId, field) => {
    return formData[categoryId]?.[questionId]?.[field] || (field.includes('rating') ? null : '')
  }

  const calculateCategoryAverage = (categoryId, ratingField) => {
    const category = formStructure.find(c => c.id === categoryId)
    if (!category) return null

    const ratings = category.questions.map(q => getFieldValue(categoryId, q.id, ratingField))
    if (ratings.length === 0 || ratings.some(rating => rating === null || rating === '' || rating === undefined)) return null

    const sum = ratings.reduce((acc, rating) => acc + parseFloat(rating), 0)
    return (sum / ratings.length).toFixed(2)
  }

  const calculateOverallCategoryAverage = (ratingField) => {
    const categoryAverages = formStructure.map(category => calculateCategoryAverage(category.id, ratingField))
    if (categoryAverages.length === 0 || categoryAverages.some(average => average === null)) return null
    return (categoryAverages.reduce((sum, average) => sum + parseFloat(average), 0) / categoryAverages.length).toFixed(2)
  }

  const categoryRatings = (categoryId, ratingField) => {
    const category = formStructure.find(item => item.id === categoryId)
    return category?.questions.map(question => getFieldValue(categoryId, question.id, ratingField)) || []
  }

  const allCompetencyRatings = ratingField => (
    formStructure.flatMap(category => categoryRatings(category.id, ratingField))
  )

  const calculateCategorySuggestedRating = (categoryId, ratingField) => competencySuggestedRating(
    categoryRatings(categoryId, ratingField),
    calculateCategoryAverage(categoryId, ratingField),
    formConfig
  )

  const updateObjective = (index, field, value) => {
    setObjectives(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row))
  }

  const totalPriority = objectives.reduce((sum, objective) => sum + (parseFloat(objective.priority) || 0), 0)

  const objectiveSelfAvg = weightedObjectiveAverage(
    objectives,
    'self_score',
    objectivesConfig.average_mode,
    objectivesConfig.allow_partial_average
  )
  const objectiveManagerAvg = weightedObjectiveAverage(
    objectives,
    'manager_score',
    objectivesConfig.average_mode,
    objectivesConfig.allow_partial_average
  )
  const objectiveSummaryConfig = objectivesConfig.summary_rating || {}
  const objectiveSummaryEnabled = Boolean(objectiveSummaryConfig.enabled)
  const employeeObjectiveRatingField = objectiveSummaryConfig.employee_field || 'employee_objective_overall_rating'
  const managerObjectiveRatingField = objectiveSummaryConfig.manager_field || 'manager_objective_overall_rating'
  const employeeObjectiveSuggestedRating = objectiveSuggestedRating(
    objectives,
    'self_score',
    objectiveSelfAvg,
    objectivesConfig
  )
  const managerObjectiveSuggestedRating = objectiveSuggestedRating(
    objectives,
    'manager_score',
    objectiveManagerAvg,
    objectivesConfig
  )

  const overallEmployeeCategoryAvg = calculateOverallCategoryAverage('employee_rating')
  const overallManagerCategoryAvg = calculateOverallCategoryAverage('manager_rating')
  const overallEmployeeAverage = overallAppraisalAverage(objectiveSelfAvg, overallEmployeeCategoryAvg)
  const overallManagerAverage = overallAppraisalAverage(objectiveManagerAvg, overallManagerCategoryAvg)
  const employeeCompetencySuggestedRating = competencySuggestedRating(
    allCompetencyRatings('employee_rating'),
    overallEmployeeCategoryAvg,
    formConfig
  )
  const managerCompetencySuggestedRating = competencySuggestedRating(
    allCompetencyRatings('manager_rating'),
    overallManagerCategoryAvg,
    formConfig
  )
  const employeeOverallSuggestedRating = overallSuggestedRating(
    employeeObjectiveSuggestedRating,
    employeeCompetencySuggestedRating,
    overallEmployeeAverage,
    formConfig
  )
  const managerOverallSuggestedRating = overallSuggestedRating(
    managerObjectiveSuggestedRating,
    managerCompetencySuggestedRating,
    overallManagerAverage,
    formConfig
  )
  const selectedManagerOverallRating = getConfiguredRatingOptions(formConfig, formData[managerOverallRatingField]).find(
    option => option.value === Number(formData[managerOverallRatingField])
  )
  const requiresOverallRatingJustification = (
    appraisal?.session_template === 'vietnam_milwaukee' &&
    requiresVietnamMilwaukeeJustification(
      managerOverallSuggestedRating,
      selectedManagerOverallRating?.label || selectedManagerOverallRating?.value
    )
  )
  const justificationMinLength = vietnamMilwaukeeJustificationMinLength(appraisal)

  const validateForm = () => {
    if (usesConfiguredAppraisalLayout(formConfig)) {
      const missing = []
      const nextObjectives = nextYearObjectives.filter(hasConfiguredObjectiveEntry)
      const nextPriorityTotal = objectivePriorityTotal(nextObjectives)
      const employeeOwnsNextYearObjectives = isConfiguredRegionalForm(formConfig)
      if (!managerOverallComment.trim()) missing.push(`Comments by ${feedbackAuthorName} are required.`)
      if (!getConfiguredOverallRatingValue(formData, formConfig)) missing.push('Overall Performance Rating is required.')
      const optionalPerformanceFactorKeys = new Set(
        formConfig.appraisal_process?.optional_performance_factor_keys || []
      )
      for (const category of formStructure) {
        const question = category.questions.find(item => item.id === 'q1') || category.questions[0]
        if (!question) continue
        const rating = getFieldValue(category.id, question.id, 'manager_rating')
        const examples = getFieldValue(category.id, question.id, 'manager_comments')
        if (!rating && !optionalPerformanceFactorKeys.has(category.id)) missing.push(`${category.title}: Rating required`)
        if (
          formConfig.appraisal_process?.examples_required === true &&
          rating &&
          !examples.trim()
        ) {
          missing.push(`${category.title}: Examples are required.`)
        }
      }
      if (!employeeOwnsNextYearObjectives) {
        const nextYearLabel = (formConfig.next_year_objectives?.title || '').replace(/\s*\n\s*/g, ' ').trim() || "Setting Next Year's Objectives"
        if (!nextObjectives.some(hasConfiguredObjectiveText)) missing.push(`${nextYearLabel}: at least one objective is required.`)
        nextObjectives.forEach(objective => {
          if (!hasConfiguredObjectiveText(objective)) {
            missing.push(`${nextYearLabel} → Row ${nextYearObjectives.indexOf(objective) + 1}: an objective is required.`)
          }
        })
        if (Math.abs(nextPriorityTotal - 100) >= 0.01) {
          missing.push(`${nextYearLabel} → Total Priority must equal 100%. Current total is ${nextPriorityTotal}%.`)
        }
      }
      missing.push(...validateMilwaukeeAsiaEnglishManagerSections(formData, formConfig))
      return missing
    }

    const missing = []
    if (isMilAsiaCheckboxFlow(appraisal, formConfig) && formConfig?.checkbox_flow?.next_year_objectives?.enabled) {
      const nextObjectives = (formData.next_year_objectives || []).filter(hasConfiguredObjectiveEntry)
      const nextPriorityTotal = objectivePriorityTotal(nextObjectives)
      if (!nextObjectives.some(hasConfiguredObjectiveText)) missing.push("Setting Next Year's Objectives: at least one objective is required.")
      if (Math.abs(nextPriorityTotal - 100) >= 0.01) {
        missing.push(`Setting Next Year's Objectives → Total Priority must equal 100%. Current total is ${nextPriorityTotal}%.`)
      }
    }
    if (usesPriorityOptions) {
      objectives.forEach((objective, index) => {
        if (
          hasAnnualObjectiveContent(objective) &&
          !allowedPriorityValues.has(`${objective.priority}`) &&
          !isLegacyNumericPriority(objective.priority)
        ) {
          missing.push(`Annual Objectives → Row ${index + 1}: priority must be ${objectivePriorityOptions.map(option => option.label || option.value).join(' or ')}.`)
        }
      })
    } else {
      objectives.forEach((objective, index) => {
        const priority = Number(objective.priority)
        if (
          hasAnnualObjectiveContent(objective) &&
          (
            objective.priority === null ||
            objective.priority === undefined ||
            objective.priority === '' ||
            !Number.isInteger(priority) ||
            priority < 0 ||
            priority > 100
          )
        ) {
          missing.push(`Annual Objectives → Row ${index + 1}: priority must be a whole number from 0 to 100.`)
        }
      })
      if (objectives.length > 0 && Math.abs(totalPriority - 100) >= 0.01) {
        missing.push(`Annual Objectives → Total Priority must equal 100%. Current total is ${totalPriority}%.`)
      }
    }
    objectives.forEach((objective, index) => {
      if (hasObjectiveText(objective) && (
        objective.manager_score === null ||
        objective.manager_score === undefined ||
        objective.manager_score === ''
      )) {
        missing.push(`Annual Objectives → Row ${index + 1}: ${feedbackAuthorName}'s rating is required.`)
      }
      if (isObjectiveScoreInvalid(objective.manager_score)) {
        missing.push(`Annual Objectives → Row ${index + 1}: ${feedbackAuthorName}'s feedback score must be between 1 and ${prcObjectiveScoreMax(appraisal, formConfig)}.`)
      }
    })
    for (const category of formStructure) {
      for (const question of category.questions) {
        const rating = getFieldValue(category.id, question.id, 'manager_rating')
        if (!rating) missing.push(`${category.title} → Q${category.questions.indexOf(question) + 1}: Rating required`)
        if (isPrcAppraisal(appraisal) && isScoreOutsideObjectiveRange(rating, 10)) {
          missing.push(`${category.title} → Q${category.questions.indexOf(question) + 1}: Rating must be between 1 and 10.`)
        }
      }
      if (
        singleCategoryComment &&
        categoryCommentRequired &&
        !String(getFieldValue(category.id, categoryCommentQuestionKey, 'manager_comments') || '').trim()
      ) {
        missing.push(`${category.title} → ${categoryCommentLabel} is required.`)
      }
    }
    if (isPrcAppraisal(appraisal)) {
      prcIdpFieldRows.forEach(([field, label]) => {
        const managerOwned = field === 'supervisor_year_end_suggestion'
        if (managerOwned && !(prcIdp[field] || '').trim()) {
          missing.push(`Individual Development Plan (IDP) → ${label} is required.`)
        }
      })
    }
    if (isMilAsiaCheckboxFlow(appraisal, formConfig) && !hasRequiredOverallCheckboxRating(formData, formConfig)) {
      missing.push('Overall Performance Rating: select one checkbox.')
    }
    if (!managerOverallComment.trim()) missing.push(`Overall Comment by ${feedbackAuthorName} is required`)
    if (
      objectiveSummaryEnabled &&
      objectiveSummaryConfig.required &&
      !formData[managerObjectiveRatingField]
    ) {
      missing.push('Annual Objectives → Overall Rating of Objective Appraisal is required.')
    }
    if (competencyManualRating.enabled && competencyManualRating.required && !formData[managerCompetencyRatingField]) {
      missing.push(`${competencySummaryTitle} is required.`)
    }
    if (overallManualRating.enabled && overallManualRating.required && !formData[managerOverallRatingField]) {
      missing.push(`${overallPerformanceTitle} is required.`)
    }
    if (
      requiresOverallRatingJustification &&
      (formData.manager_overall_rating_justification || '').trim().length < justificationMinLength
    ) {
      missing.push(`${overallPerformanceTitle} justification must be at least ${justificationMinLength} characters.`)
    }
    return missing
  }

  const getDraftPayload = () => {
    const isConfiguredLayout = usesConfiguredAppraisalLayout(formConfig)
    return {
      form_data: {
        ...formData,
        ...(isConfiguredLayout
          ? {
            objectives: objectives.filter(hasConfiguredObjectiveEntry),
            next_year_objectives: nextYearObjectives.filter(hasConfiguredObjectiveEntry)
          }
          : { annual_objectives: objectives })
      },
      manager_overall_comment: managerOverallComment,
      ...(!isFunctionalReview ? { draft_return_comments: returnComments } : {})
    }
  }

  async function saveDraft() {
    const maxScore = prcObjectiveScoreMax(appraisal, formConfig)
    const invalidScoreIndex = objectives.findIndex(objective => isObjectiveScoreInvalid(objective.manager_score))
    if (invalidScoreIndex >= 0) {
      message.error(`Annual Objectives row ${invalidScoreIndex + 1}: ${feedbackAuthorName}'s feedback score must be between 1 and ${maxScore}.`)
      return false
    }
    setSavingDraft(true)
    try {
      const updated = appraisal.active_functional_review
        ? await api.functionalReviewDraft(id, getDraftPayload())
        : await api.managerDraft(id, getDraftPayload())
      setAppraisal(updated)
      message.success('Draft saved')
      return true
    } catch (e) {
      message.error(e.message || 'Failed to save draft')
      return false
    } finally {
      setSavingDraft(false)
    }
  }

  async function submit() {
    if (!isFunctionalReview && isReturnedForRevision && !resubmissionRemarks.trim()) {
      setReturnDrawerOpen(true)
      setCommentRecipient('approver')
      setShowValidationErrors(true)
      message.error('A response to the returning approver is required.')
      return
    }
    const errors = appraisal.active_functional_review ? [] : validateForm()
    if (errors.length > 0) {
      setShowValidationErrors(true)
      Modal.error({
        title: 'Please complete all fields before submitting',
        width: 720,
        content: (
          <div style={{ maxHeight: '60vh', overflowY: 'auto', marginTop: 12 }}>
            <ol style={{ paddingLeft: 20, marginBottom: 0 }}>
              {errors.map((error, index) => (
                <li key={index} style={{ marginBottom: 8 }}>{error}</li>
              ))}
            </ol>
          </div>
        )
      })
      return
    }
    setSubmitting(true)
    try {
      if (appraisal.active_functional_review) {
        await api.functionalReviewSubmit(id, getDraftPayload())
      } else {
        await api.managerSubmit(id, { ...getDraftPayload(), resubmission_remarks: resubmissionRemarks })
      }
      navigate('/dashboard')
    } catch (e) {
      message.error(e.message)
    }
    setSubmitting(false)
  }

  async function submitReturnToEmployee() {
    if (isReadOnly || isFunctionalReview) return
    if (!returnComments.trim()) {
      setReturnDrawerOpen(true)
      message.error('A return reason is required.')
      return
    }
    setReturningToEmployee(true)
    try {
      await api.returnAppraisalToPreviousStage(id, returnComments.trim(), getDraftPayload())
      setReturnDrawerOpen(false)
      navigate('/dashboard')
    } catch (e) {
      message.error(e.message)
    } finally {
      setReturningToEmployee(false)
    }
  }

  if (loading || structureLoading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin size='large' /></div>
  if (!appraisal) return <Alert type='error' message='Appraisal not found' />
  if (structureError) {
    return <Alert type='error' showIcon message='Unable to load the appraisal template' description='Refresh the page or contact HR if the problem continues.' />
  }
  if (Number(appraisal.employee) === Number(currentUser?.id)) {
    return <Navigate to={`/appraisals/${id}/employee`} replace />
  }

  const isAssignedDirectApprover = appraisal.approver_1 === currentUser?.id
  const isAssignedViewingApprover = viewerApproverLevel &&
    appraisal[`approver_${viewerApproverLevel}`] === currentUser?.id
  if (!isFunctionalReview && !isAssignedDirectApprover && !(isViewingReviewerFeedback && isAssignedViewingApprover)) {
    return <Navigate to='/dashboard' replace />
  }
  const isReadOnly = isViewingReviewerFeedback || (isFunctionalReview
    ? appraisal.active_functional_review.status !== 'pending' || appraisal.status !== 'pending_manager'
    : appraisal.status !== 'pending_manager' || !isAssignedDirectApprover)
  const awaitingEmployee = appraisal.status === 'pending_employee'
  const assignedToAnotherApprover = !isFunctionalReview && appraisal.status === 'pending_manager' && !isAssignedDirectApprover
  const theme = getBusinessUnitTheme(appraisal)
  const isConfiguredLayout = usesConfiguredAppraisalLayout(formConfig)
  const isPrc = isPrcAppraisal(appraisal)
  const objectiveScoreMax = prcObjectiveScoreMax(appraisal, formConfig)
  const objectiveScoreLabel = prcObjectiveScoreLabel(appraisal, formConfig)
  const prcObjectives = prcObjectiveConfig(formConfig)
  const prcSuccessIndicatorLevels = prcObjectives.success_indicator_levels || []
  const prcIdpFieldRows = prcIdpFields(formConfig)
  const prcEmployeeInfoFields = prcEmployeeInformationFields(appraisal, formConfig)
  const prcIdp = formData.prc_idp || {}
  const isCheckboxFlow = isMilAsiaCheckboxFlow(appraisal, formConfig)
  const ratingGuideSource = checkboxRatingGuideSource(appraisal, formConfig)
  const submitActionLabel = isFunctionalReview
    ? 'Send Back to Direct Manager'
    : nextApproverName === 'No further approval'
      ? 'Submit appraisal'
      : `Submit appraisal to ${nextApproverName}`
  const updatePrcIdp = (field, value) => setFormData(prev => ({
    ...prev,
    prc_idp: {
      ...(prev.prc_idp || {}),
      [field]: value
    }
  }))

  const updateNextYearObjective = (index, field, value) => {
    setNextYearObjectives(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row))
  }
  const removeNextYearObjective = index => {
    setNextYearObjectives(prev => prev.filter((_, rowIndex) => rowIndex !== index))
  }
  const busy = savingDraft || submitting || returningToEmployee
  const replyingToApprover = isReturnedForRevision && commentRecipient === 'approver'
  const returningApproverName = appraisal.returned_by_name || appraisal.returned_by_email || 'the returning approver'
  const composerLabel = replyingToApprover
    ? `Response to ${returningApproverName} (REQUIRED)`
    : 'Feedback to Employee'
  const conversationSidebar = (
    <AppraisalConversationSidebar
      appraisal={appraisal}
      feedback={(
        <ManagerFeedback
          appraisal={{ ...appraisal, manager_overall_comment: managerOverallComment }}
          formData={formData}
          formStructure={formStructure}
          objectives={objectives}
          formConfig={formConfig}
          ratingDisplay={value => isPrc
            ? `${value} - ${prcEvaluationLevel(value)}`
            : isConfiguredLayout || usesConfiguredRatings
              ? getConfiguredRatingDisplay(value, formConfig)
              : getRatingValueDisplay(value, ratingGuideSource)}
          authorName={feedbackAuthorName}
        />
      )}
      composer={!isReadOnly && !isFunctionalReview ? (
        <div>
          {isReturnedForRevision && (
            <Select
              aria-label='Comment recipient'
              value={commentRecipient}
              onChange={setCommentRecipient}
              disabled={busy}
              style={{ width: '100%', marginBottom: 12 }}
              options={[
                { value: 'approver', label: `Returning approver: ${returningApproverName}` },
                { value: 'employee', label: 'Employee' }
              ]}
            />
          )}
          <Text strong style={{ display: 'block', marginBottom: 8 }}>{composerLabel}</Text>
          <TextArea
            aria-label={composerLabel}
            rows={3}
            value={replyingToApprover ? resubmissionRemarks : returnComments}
            onChange={event => replyingToApprover
              ? setResubmissionRemarks(event.target.value)
              : setReturnComments(event.target.value)}
            disabled={busy}
            status={replyingToApprover && showValidationErrors && !resubmissionRemarks.trim() ? 'error' : undefined}
            placeholder={replyingToApprover
              ? 'Explain how you addressed the requested changes'
              : 'Enter a required reason to return the appraisal to the employee'}
          />

        </div>
      ) : null}
      actions={!isReadOnly && !isFunctionalReview ? (
        replyingToApprover ? (
          <Popconfirm
            title='Are you sure?'
            description='Please confirm you want to submit.'
            okText={submitActionLabel}
            cancelText='Cancel'
            onConfirm={submit}
            disabled={busy}
          >
            <Button type='link' loading={submitting} disabled={busy}>Send</Button>
          </Popconfirm>
        ) : (
          <Button
            type='link'
            className='tti-submit-feedback'
            onClick={submitReturnToEmployee}
            loading={returningToEmployee}
            disabled={busy}
          >
            <span>Submit Feedback</span>
            <SubmitFeedbackIcon />
          </Button>
        )
      ) : null}
      open={returnDrawerOpen}
      onOpen={() => setReturnDrawerOpen(true)}
      onClose={() => setReturnDrawerOpen(false)}
    />
  )
  const stickyActions = !isReadOnly && (
    <div className='tti-form-action-bar tti-appraisal-action-bar'>
      <div className='tti-form-action-bar__inner'>
        <Button onClick={saveDraft} loading={savingDraft} disabled={busy} className='tti-btn-secondary tti-form-action-bar__draft'>Save Draft</Button>
        <Space wrap className='tti-appraisal-actions'>
          <Button
            onClick={() => setReturnDrawerOpen(true)}
            disabled={busy || returnDrawerOpen}
            className='tti-btn-primary-grey tti-send-back-button'
          >
            Send back to {employeeName}
          </Button>
          <Popconfirm
            title='Are you sure?'
            description='Please confirm you want to submit.'
            okText={submitActionLabel}
            cancelText='Cancel'
            onConfirm={submit}
            disabled={busy}
          >
            <Button type='primary' loading={submitting} disabled={busy} style={{ color: '#FFFFFF' }}>
              Submit Appraisal
            </Button>
          </Popconfirm>
        </Space>
      </div>
    </div>
  )
  const functionalReviews = !isFunctionalReview ? (appraisal.functional_reviews || []) : []
  const renderManagerReviewTabs = reviewContent => {
    if (!functionalReviews.length) return reviewContent

    const reviewerTabKeys = functionalReviews.map(review => `reviewer-feedback-${review.id}`)
    const requestedTabKey = reviewId ? `reviewer-feedback-${reviewId}` : 'appraisal-form'
    const activeTabKey = reviewerTabKeys.includes(requestedTabKey) ? requestedTabKey : 'appraisal-form'

    return (
      <Tabs
        activeKey={activeTabKey}
        onChange={(key) => {
          if (key === 'appraisal-form') {
            navigate(appraisalFormRoute, { replace: true })
            return
          }
          const matchedReviewId = key.replace('reviewer-feedback-', '')
          navigate(`${appraisalFormRoute}/feedback/${matchedReviewId}`, { replace: true })
        }}
        items={[
          { key: 'appraisal-form', label: 'Appraisal Form', children: reviewContent },
          ...functionalReviews.map((review, index) => ({
            key: `reviewer-feedback-${review.id}`,
            label: review.reviewer_name || review.reviewer_email || `Reviewer ${index + 1}`,
            children: reviewContent
          }))
        ]}
      />
    )
  }

  if (isConfiguredLayout) {
    const submittedObjectives = objectives.filter(hasConfiguredObjectiveText)
    const nextObjectives = nextYearObjectives
    const nextPriorityTotal = objectivePriorityTotal(nextObjectives.filter(hasConfiguredObjectiveEntry))
    const configuredForm = formConfig.appraisal_form || {}
    const showEnglishSections = isConfiguredRegionalForm(formConfig)
    const employeeInformation = formConfig.employee_information || {}
    const employeeInformationFields = (employeeInformation.fields?.length
      ? employeeInformation.fields
      : [
        { key: 'employee_name', label: 'Employee Name' },
        { key: 'employee_number', label: 'Employee Number' },
        { key: 'employee_position', label: 'Employee Position' },
        { key: 'manager_name', label: 'Manager Name' },
        { key: 'department_name', label: 'Department Name' }
      ]).filter(field => field.key !== 'department_code')
    const employeeInformationValues = {
      employee_name: employeeName,
      employee_number: appraisal.employee_company_id,
      employee_position: appraisal.employee_position,
      manager_name: appraisal.manager_name,
      department_name: appraisal.department_name,
      performance_period: `${formatDate(appraisal.effective_review_period_start_date)} - ${formatDate(appraisal.effective_review_period_end_date)}`
    }
    const employeeRatingField = configuredForm.employee_overall_rating_field || 'employee_overall_rating'
    const maximumNextYearObjectives = formConfig.next_year_objectives?.maximum_rows
    const hasAchievementExamples = Object.prototype.hasOwnProperty.call(
      configuredForm.objective_row || {},
      'achievement_examples'
    ) || /(^|_)tti$/.test(appraisal.session_template || '') ||
      submittedObjectives.some(row => Object.prototype.hasOwnProperty.call(row, 'achievement_examples'))

    return (
      <div className='tti-appraisal-layout' style={businessUnitThemeVars(theme)}>
        <div className='tti-appraisal-form' style={{ flex: 1, minWidth: 0, maxWidth: 1600, margin: '0 auto', padding: '20px' }}>
          <div style={{ marginBottom: 8 }}>
            <BackButton type='text' fallback='/dashboard' className='tti-appraisal-back-button' aria-label='Back' />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', marginBottom: 24 }}>
            <div>
              {theme.logoUrl && <img src={theme.logoUrl} alt={appraisal.session_template_name || 'Template logo'} style={{ display: 'block', maxHeight: 64, maxWidth: 180, objectFit: 'contain', marginBottom: 12 }} />}
              <Title level={3} className='tti-page-title' style={{ marginBottom: 4 }}>{appraisal.session_display_title || '2026 Performance Appraisal'}</Title>
              <Text type='secondary'>{formConfig.employee_information?.employee_label || 'Employee'}: {employeeName}</Text>
            </div>
          </div>

          <Collapse
            className='tti-collapsible-form-card'
            style={{ marginBottom: 24 }}
            expandIconPlacement='end'
            items={[{
              key: 'employee-information',
              label: (
                <Space wrap>
                  <Text strong>{employeeInformation.title || 'Employee Information'}</Text>
                  <Text type='secondary'>
                    {[employeeName, appraisal.employee_position, appraisal.department_name].filter(Boolean).join(' · ')}
                  </Text>
                </Space>
              ),
              children: (
                <Row gutter={[24, 16]} className='tti-collapsible-fields'>
                  {employeeInformationFields.map(field => (
                    <Col span={8} key={field.key}>
                      <Text type='secondary' style={{ fontSize: 12 }}>{field.label}</Text>
                      <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                        {employeeInformationValues[field.key] || '--'}
                      </div>
                    </Col>
                  ))}
                </Row>
              )
            }]}
          />
          {!showEnglishSections && formConfig.appraisal_process?.show_rating_scale_table !== true && (
            <Collapse
              className='tti-collapsible-form-card'
              style={{ marginBottom: 12 }}
              expandIconPlacement='end'
              items={[{
                key: 'rating-guide',
                label: <Space><InfoCircleOutlined /><Text strong>Rating Scale Guide</Text><Text type='secondary'>View scoring definitions</Text></Space>,
                children: <RatingGuideDescription source={formConfig} />
              }]}
            />
          )}

          {renderManagerReviewTabs(
            <>
              <Card className='tti-appraisal-section-card' title={<Text style={{ color: '#FFF' }}>{formConfig.objectives?.title || 'I. Appraisal Year Objectives'}</Text>} style={{ marginBottom: 24 }}>
                {submittedObjectives.length
                  ? submittedObjectives.map((objective, index) => (
                    <Row key={index} gutter={8} style={{ marginBottom: 12 }}>
                      <Col span={4}><Text strong>{objective.priority || 0}%</Text></Col>
                      <Col span={hasAchievementExamples ? 10 : 20}>
                        <Text style={{ whiteSpace: 'pre-wrap' }}>{objective.objective}</Text>
                      </Col>
                      {hasAchievementExamples && (
                        <Col span={10}>
                          <Text type='secondary' style={{ whiteSpace: 'pre-wrap' }}>
                            {objective.achievement_examples || 'No achievement examples provided.'}
                          </Text>
                        </Col>
                      )}
                    </Row>
                  ))
                  : <Text type='secondary'>No objectives submitted.</Text>}
              </Card>

              {showEnglishSections && (
                <Card className='tti-appraisal-section-card' title={<Text style={{ color: '#FFF' }}>{formConfig.employee_comments?.title || "Employee's Comments"}</Text>} style={{ marginBottom: 24 }}>
                  <Text style={{ whiteSpace: 'pre-wrap' }}>{appraisal.employee_overall_comment || 'No employee comments provided.'}</Text>
                </Card>
              )}

              {showEnglishSections && (
                <MilwaukeeAsiaEnglishSections
                  formData={formData}
                  formConfig={formConfig}
                  mode='read'
                  showDevelopmentPlan={false}
                  showManagerFields={false}
                  cultureSide='employee'
                />
              )}

              {showEnglishSections && (
                <MilwaukeeAsiaEnglishSections
                  formData={formData}
                  formConfig={formConfig}
                  mode={isReadOnly ? 'read' : 'manager'}
                  onChange={setFormData}
                  showDevelopmentPlan={false}
                  cultureSide='manager'
                  showValidationErrors={showValidationErrors}
                />
              )}

              {!showEnglishSections && (
                <>
                  <Card className='tti-appraisal-section-card' title={<Text style={{ color: '#FFF' }}>II. Appraisal Process</Text>} style={{ marginBottom: 24 }}>
                    <div style={{ marginBottom: 16 }}>
                      <Text strong>Overall Performance - Comments by {feedbackAuthorName}</Text>
                      <TextArea
                        rows={5}
                        value={managerOverallComment}
                        onChange={e => setManagerOverallComment(e.target.value)}
                        status={showValidationErrors && !managerOverallComment.trim() ? 'error' : undefined}
                        disabled={isReadOnly}
                        placeholder={`Comments by ${feedbackAuthorName}...`}
                      />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <Text strong type={isConfiguredManagerOverallRatingMissing ? 'danger' : undefined}>Overall Performance Rating</Text>
                      <Select
                        style={{ width: '100%', marginTop: 8 }}
                        value={getConfiguredOverallRatingValue(formData, formConfig)}
                        onChange={value => setFormData(prev => ({ ...prev, [getConfiguredOverallRatingField(formConfig)]: value }))}
                        disabled={isReadOnly}
                        placeholder='Select overall rating...'
                        allowClear
                        status={isConfiguredManagerOverallRatingMissing ? 'error' : undefined}
                      >
                        {getConfiguredOverallRatingOptions(formConfig).map(option => (
                          <Option key={option.value} value={option.value}>
                            {ratingOptionText(option)}: {option.description}
                          </Option>
                        ))}
                      </Select>
                      {isConfiguredManagerOverallRatingMissing && (
                        <Text type='danger' style={{ display: 'block', marginTop: 4 }}>
                          Overall Performance Rating is required.
                        </Text>
                      )}
                    </div>
                  </Card>

                  {isConfiguredFormSectionEnabled(formConfig, 'manager', 'performance_factors') && (
                    <PerformanceFactorsCard
                      formStructure={formStructure}
                      formConfig={formConfig}
                      getFieldValue={getFieldValue}
                      updateField={updateField}
                      getQuestionRatingOptions={() => getConfiguredFactorRatingOptions(formConfig)}
                      getRatingOptionLabel={option => ratingOptionText(option)}
                      getRatingValueDisplay={value => {
                        const option = getConfiguredFactorRatingOptions(formConfig).find(item => item.value === value)
                        return option ? ratingOptionText(option) : value
                      }}
                      editableSide={isReadOnly ? null : 'manager'}
                      visibleSide='manager'
                      managerSubmitted={displayedManagerFeedbackAvailable}
                      showValidationErrors={showValidationErrors}
                    />
                  )}

                  <Card className='tti-appraisal-section-card' title={<Text style={{ color: '#FFF' }}>III. Employee's Comments</Text>} style={{ marginBottom: 24 }}>
                    <Text style={{ whiteSpace: 'pre-wrap' }}>{appraisal.employee_overall_comment || 'No employee comments provided.'}</Text>
                  </Card>
                </>
              )}

              <Card
                className='tti-appraisal-section-card'
                title={<Text style={{ color: '#FFF', whiteSpace: 'pre-line' }}>{formConfig.next_year_objectives?.title || "IV. Setting Next Year's Objectives"}</Text>}
                style={{ marginBottom: 24 }}
              >
                <Text type='secondary' style={{ display: 'block', marginBottom: 16, whiteSpace: 'pre-line' }}>
                  {showEnglishSections
                    ? (formConfig.next_year_objectives?.description || 'These objectives were provided by the employee.')
                    : "Determine next year's objectives with the employee. Priority total should add up to 100%."}
                </Text>
                {formConfig.next_year_objectives?.objectives_note && (
                  <Text type='secondary' style={{ display: 'block', marginBottom: 16, paddingTop: 12, borderTop: '1px solid var(--neutral-grey2)', whiteSpace: 'pre-line' }}>
                    {renderBoldMarkup(formConfig.next_year_objectives.objectives_note)}
                  </Text>
                )}
                <div style={{ marginBottom: 12, borderBottom: '2px solid #d9d9d9' }}>
                  <Row gutter={8}>
                    <Col span={4}><div style={{ padding: '8px 4px', fontWeight: 600, textAlign: 'center', background: 'var(--neutral-grey0)', fontSize: 14, whiteSpace: 'pre-line' }}>{formConfig.next_year_objectives?.priority_label || 'Weighting (%)'}</div></Col>
                    <Col span={20}><div style={{ padding: '8px 4px', fontWeight: 600, textAlign: 'center', background: 'var(--neutral-grey0)', fontSize: 14, whiteSpace: 'pre-line' }}>{formConfig.next_year_objectives?.objective_label || 'Annual Performance Objectives'}</div></Col>
                  </Row>
                </div>
                {nextObjectives.map((objective, index) => (
                  <div key={index} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: index < nextObjectives.length - 1 ? '1px solid var(--neutral-grey2)' : 'none' }}>
                    <Row gutter={8} align='top'>
                      <Col span={4}>
                        <Input
                          type='number'
                          min={0}
                          max={100}
                          step={1}
                          value={objective.priority}
                          onChange={e => {
                            if (isWholeNumberInput(e.target.value)) updateNextYearObjective(index, 'priority', e.target.value)
                          }}
                          disabled={isReadOnly || showEnglishSections}
                          suffix='%'
                        />
                      </Col>
                      <Col span={20}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <TextArea
                            rows={2}
                            value={objective.objective}
                            onChange={e => updateNextYearObjective(index, 'objective', e.target.value)}
                            disabled={isReadOnly || showEnglishSections}
                            placeholder='Describe objective...'
                            style={{ fontSize: 14, flex: 1 }}
                          />
                          {!isReadOnly && !showEnglishSections && nextObjectives.length > 1 && (
                            <Popconfirm title='Remove this objective?' onConfirm={() => removeNextYearObjective(index)} okText='Remove' okButtonProps={{ danger: true }}>
                              <Button type='text' danger icon={<DeleteOutlined />} aria-label={`Remove next-year objective ${index + 1}`} />
                            </Popconfirm>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </div>
                ))}
                {!isReadOnly && !showEnglishSections && (!maximumNextYearObjectives || nextObjectives.length < maximumNextYearObjectives) && (
                  <div style={{ marginBottom: 12 }}>
                    <Button size='large' className='tti-btn-primary-grey' icon={<PlusOutlined />} onClick={() => setNextYearObjectives(prev => [...prev, { ...getConfiguredObjectiveRow(formConfig) }])}>Add Objective</Button>
                  </div>
                )}
                <Text type={Math.abs(nextPriorityTotal - 100) < 0.01 ? 'success' : 'danger'} strong>
                  Total Priority: {nextPriorityTotal}% {Math.abs(nextPriorityTotal - 100) < 0.01 ? '✓' : '(must equal 100%)'}
                </Text>
              </Card>

              {showEnglishSections && (
                <MilwaukeeAsiaEnglishSections
                  formData={formData}
                  formConfig={formConfig}
                  mode='read'
                  showCulture={false}
                />
              )}

              {showEnglishSections && (
                <Card
                  className='tti-appraisal-section-card'
                  title={(
                    <Text strong style={{ whiteSpace: 'pre-line' }}>
                      {formConfig.appraisal_process?.manager_comments_label || "Manager's Overall Comments"}
                    </Text>
                  )}
                  style={{ marginBottom: 24 }}
                >
                  <TextArea
                    rows={5}
                    value={managerOverallComment}
                    onChange={e => setManagerOverallComment(e.target.value)}
                    status={showValidationErrors && !managerOverallComment.trim() ? 'error' : undefined}
                    disabled={isReadOnly}
                    placeholder={`Comments by ${feedbackAuthorName}...`}
                  />
                </Card>
              )}

              {showEnglishSections && isConfiguredFormSectionEnabled(formConfig, 'manager', 'performance_factors') && (
                <PerformanceFactorsCard
                  formStructure={formStructure}
                  formConfig={formConfig}
                  getFieldValue={getFieldValue}
                  updateField={updateField}
                  getQuestionRatingOptions={() => getConfiguredFactorRatingOptions(formConfig)}
                  getRatingOptionLabel={option => ratingOptionText(option)}
                  getRatingValueDisplay={value => {
                    const option = getConfiguredFactorRatingOptions(formConfig).find(item => item.value === value)
                    return option ? ratingOptionText(option) : value
                  }}
                  editableSide={isReadOnly ? null : 'manager'}
                  visibleSide='manager'
                  managerSubmitted={displayedManagerFeedbackAvailable}
                  showValidationErrors={showValidationErrors}
                />
              )}

              {showEnglishSections && (
                <Card
                  className='tti-appraisal-section-card'
                  title={(
                    <Text strong style={{ whiteSpace: 'pre-line' }}>
                      {formConfig.appraisal_process?.employee_overall_rating_title ||
                        `${formConfig.appraisal_process?.overall_rating_label || 'Overall Performance Rating'} - ${formConfig.appraisal_process?.employee_evaluation_label || 'Employee Self-Rating'}`}
                    </Text>
                  )}
                  style={{ marginBottom: 24 }}
                >
                  <Text>{configuredRatingLabel(formData[employeeRatingField], formConfig)}</Text>
                </Card>
              )}

              {showEnglishSections && (
                <Card
                  className='tti-appraisal-section-card'
                  title={(
                    <Text strong style={{ whiteSpace: 'pre-line' }}>
                      {configuredManagerOverallRatingTitle}
                    </Text>
                  )}
                  style={{ marginBottom: 24 }}
                >
                  {formConfig.appraisal_process?.overall_rating_description !== '' && (
                    <Text type='secondary' style={{ display: 'block', marginBottom: 8, whiteSpace: 'pre-line' }}>
                      {formConfig.appraisal_process?.overall_rating_description || "Select the rating that best describes the employee's overall performance."}
                    </Text>
                  )}
                  <Select
                    style={{ width: '100%' }}
                    value={getConfiguredOverallRatingValue(formData, formConfig)}
                    onChange={value => setFormData(prev => ({ ...prev, [getConfiguredOverallRatingField(formConfig)]: value }))}
                    disabled={isReadOnly}
                    placeholder='Select manager rating...'
                    allowClear
                    status={isConfiguredManagerOverallRatingMissing ? 'error' : undefined}
                  >
                    {getConfiguredOverallRatingOptions(formConfig).map(option => (
                      <Option key={option.value} value={option.value}>
                        {ratingOptionText(option)}
                      </Option>
                    ))}
                  </Select>
                  {isConfiguredManagerOverallRatingMissing && (
                    <Text type='danger' style={{ display: 'block', marginTop: 4 }}>
                      Overall Performance Rating is required.
                    </Text>
                  )}
                </Card>
              )}

              <Card className='tti-appraisal-section-card' title={<Text style={{ color: '#FFF' }}>Reviewed / Approvals</Text>}>
                <Row gutter={[16, 16]}>
                  <Col span={12}><Text strong>Employee Signature</Text><div>{personLabel(appraisal.employee_first_name, appraisal.employee_last_name, appraisal.employee_email, '', appraisal.session_template)}</div></Col>
                  <Col span={12}><Text strong>Signed by</Text><div>{feedbackAuthorName}</div></Col>
                  <Col span={12}><Text strong>Next approver</Text><div>{nextApproverName}</div></Col>
                  <Col span={12}><Text strong>Overall Rating</Text><div>{configuredRatingLabel(getConfiguredOverallRatingValue(formData, formConfig), formConfig)}</div></Col>
                </Row>
              </Card>
              {stickyActions}
            </>
          )}
        </div>
        {conversationSidebar}
      </div>
    )
  }

  return (
    <div className='tti-appraisal-layout' style={businessUnitThemeVars(theme)}>
      <div className='tti-appraisal-form' style={{ flex: 1, minWidth: 0, maxWidth: 1600, margin: '0 auto', padding: '20px' }}>
        <div style={{ marginBottom: 8 }}>
          <BackButton type='text' fallback='/dashboard' className='tti-appraisal-back-button' aria-label='Back' />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            {theme.logoUrl && <img src={theme.logoUrl} alt={appraisal.session_template_name || 'Template logo'} style={{ display: 'block', maxHeight: 64, maxWidth: 180, objectFit: 'contain', marginBottom: 12 }} />}
            <Title level={3} className='tti-page-title' style={{ marginBottom: 4 }}>{appraisal.session_display_title || '2026 Performance Appraisal'}</Title>
            <Text type='secondary' style={{ fontSize: 14 }}>(for Staff Level & above)</Text>
            <br />
            <Text type='secondary'>Employee: {employeeName}</Text>
          </div>
        </div>

        {renderManagerReviewTabs(
          <>
            {/* Rating Guide */}
            <Collapse
              className='tti-collapsible-form-card'
              style={{ marginBottom: 12 }}
              expandIconPlacement='end'
              items={[{
                key: 'rating-guide',
                label: <Space><InfoCircleOutlined /><Text strong>Rating Scale Guide</Text><Text type='secondary'>View scoring definitions</Text></Space>,
                children: isPrc ? <PrcRatingGuideDescription /> : <RatingGuideDescription source={ratingGuideSource} />
              }]}
            />

            {/* Employee Information */}
            <Collapse
              className='tti-collapsible-form-card'
              style={{ marginBottom: 24 }}
              expandIconPlacement='end'
              items={[{
                key: 'employee-information',
                label: (
                  <Space wrap>
                    <Text strong>{isPrc ? prcEmployeeInformationTitle(formConfig) : 'Employee Information'}</Text>
                    <Text type='secondary'>
                      {[personLabel(appraisal.employee_first_name, appraisal.employee_last_name, appraisal.employee_email, '', appraisal.session_template), appraisal.employee_position, appraisal.department_name].filter(Boolean).join(' · ')}
                    </Text>
                  </Space>
                ),
                children: (
                  <Row gutter={[24, 16]} className='tti-collapsible-fields'>
                    {isPrc
                      ? prcEmployeeInfoFields.map(field => (
                        <Col span={8} key={field.key}>
                          <Text type='secondary' style={{ fontSize: 12 }}>{field.label}:</Text>
                          <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                            {field.value}
                          </div>
                        </Col>
                      ))
                      : (
                        <>
                          <Col span={8}>
                            <Text type='secondary' style={{ fontSize: 12 }}>Fullname:</Text>
                            <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                              {personLabel(appraisal.employee_first_name, appraisal.employee_last_name, appraisal.employee_email, '', appraisal.session_template)}
                            </div>
                          </Col>
                          <Col span={8}>
                            <Text type='secondary' style={{ fontSize: 12 }}>Emp.ID:</Text>
                            <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                              {appraisal.employee_company_id || appraisal.employee || '—'}
                            </div>
                          </Col>
                          <Col span={8}>
                            <Text type='secondary' style={{ fontSize: 12 }}>Position:</Text>
                            <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>{appraisal.employee_position || '—'}</div>
                          </Col>
                          <Col span={8}>
                            <Text type='secondary' style={{ fontSize: 12 }}>Dept.:</Text>
                            <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                              {appraisal.department_name || '—'}
                            </div>
                          </Col>
                          <Col span={8}><Text type='secondary' style={{ fontSize: 12 }}>Manager Name:</Text><div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>{appraisal.manager_name || '—'}</div></Col>
                          <Col span={8}>
                            <Text type='secondary' style={{ fontSize: 12 }}>Joined Date ({DATE_FORMAT_LABEL}):</Text>
                            <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                              {formatDate(appraisal.employee_date_joined)}
                            </div>
                          </Col>

                          <Col span={8}>
                            <Text type='secondary' style={{ fontSize: 12 }}>Review Period From ({DATE_FORMAT_LABEL}):</Text>
                            <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                              {formatDate(appraisal.effective_review_period_start_date)}
                            </div>
                          </Col>
                          <Col span={8}>
                            <Text type='secondary' style={{ fontSize: 12 }}>Review Period To ({DATE_FORMAT_LABEL}):</Text>
                            <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                              {formatDate(appraisal.effective_review_period_end_date)}
                            </div>
                          </Col>
                          <Col span={8} />

                          <Col span={8}>
                            <Text type='secondary' style={{ fontSize: 12, color: theme.primaryColor, fontStyle: 'normal' }}>Evaluator Name:</Text>
                            <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                              {personLabel(appraisal.approver_1_first_name ?? appraisal.manager_first_name, appraisal.approver_1_last_name ?? appraisal.manager_last_name, appraisal.approver_1_email ?? appraisal.manager_email, '', appraisal.session_template)}
                            </div>
                          </Col>
                          <Col span={8}>
                            <Text type='secondary' style={{ fontSize: 12, color: theme.primaryColor, fontStyle: 'normal' }}>Evaluation Date ({DATE_FORMAT_LABEL}):</Text>
                            <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                              {formatDate(displayedManagerSubmittedAt)}
                            </div>
                          </Col>
                          <Col span={8}>
                            <Text type='secondary' style={{ fontSize: 12, color: theme.primaryColor, fontStyle: 'normal' }}>Evaluator ID:</Text>
                            <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                              {appraisal.manager_company_id || '--'}
                            </div>
                          </Col>
                        </>
                      )}
                    {appraisal.session_organization_structure !== 'department_only' && (
                      <Col span={8}>
                        <Text type='secondary' style={{ fontSize: 12 }}>Business Unit:</Text>
                        <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                          {appraisal.business_unit_name || '—'}
                        </div>
                      </Col>
                    )}
                  </Row>
                )
              }]}
            />

            {isCheckboxFlow && (
              <OverallPerformanceRatingCheckboxes
                formData={formData}
                formConfig={formConfig}
                disabled={isReadOnly}
                onChange={value => setCheckboxFlowData(setFormData, { overall_performance_rating: value })}
              />
            )}

            {/* Annual Objectives Appraisal */}
            <Card
              className='tti-appraisal-section-card'
              style={{ marginBottom: 24 }}
              title={<Text style={{ color: '#FFF' }}>{objectivesConfig.title || 'Annual Objectives Appraisal'}</Text>}
            >
              {(appraisal.session_template || appraisal.session_business_unit || '').includes('milwaukee') && (
                <div style={{ marginBottom: 16 }}>
                  <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 16, color: 'var(--bu-ink)' }}>List and prioritize your key objectives following the S.M.A.R.T principles</Text>
                  <ol style={{ margin: 0, paddingLeft: 20, fontSize: 14, color: 'var(--bu-ink)' }}>
                    <li><Text strong>S – Specific:</Text> clear and well-defined (What exactly do I want to achieve? Why is it important?)</li>
                    <li><Text strong>M – Measurable:</Text> can be tracked using metrics or indicators (How will I measure success? What evidence shows progress?)</li>
                    <li><Text strong>A – Achievable:</Text> realistic and attainable (Can I reach this goal with my current skills and resources?)</li>
                    <li><Text strong>R – Relevant:</Text> aligns with team, department, or company objectives (Does this goal support broader organizational priorities?)</li>
                    <li><Text strong>T – Time-bound:</Text> clear deadline or timeframe for completion (When will I achieve this goal? What are the milestones?)</li>
                  </ol>
                </div>
              )}
              <Text style={{ display: 'block', marginBottom: 16, color: 'var(--bu-ink)' }}>
                {isPrc
                  ? 'List up to ten SMART performance objectives. Weight total must add up to 100%. Include success indicators for each objective.'
                  : (objectivesConfig.description || 'List key objectives and prioritize using percentage assigned. As a percent of total job, and it should add up to 100%.')}
              </Text>

              {objectives.length > 0
                ? (
                  <>
                    {/* Header */}
                    <div style={{ marginBottom: 12, borderBottom: '1px solid var(--tti-light-grey)' }}>
                      <Row gutter={8}>
                        <Col span={2}><div style={{ padding: '8px 4px', fontWeight: 600, textAlign: 'center', background: 'var(--neutral-grey1)', fontSize: 14 }}>{objectivesConfig.priority_label || '% Priority'}</div></Col>
                        <Col span={isPrc ? 5 : 6}><div style={{ padding: '8px 4px', fontWeight: 600, textAlign: 'center', background: 'var(--neutral-grey1)', fontSize: 14 }}>{isPrc ? prcObjectives.objective_label : (objectivesConfig.objective_label || 'Annual Performance Objectives')}</div></Col>
                        {isPrc && <Col span={5}><div style={{ padding: '8px 4px', fontWeight: 600, textAlign: 'center', background: 'var(--neutral-grey1)', fontSize: 14 }}>{prcObjectives.success_indicators_label}</div></Col>}
                        <Col span={isPrc ? 6 : 8}>
                          <div style={{ padding: '6px 0', fontWeight: 600, textAlign: 'center', background: 'var(--neutral-grey2)', fontSize: 14 }}>Self-Evaluation</div>
                          <Row gutter={4}>
                            <Col span={10}><div style={{ padding: '4px 0', fontSize: 13, fontWeight: 500, textAlign: 'center', background: 'var(--neutral-grey2)' }}>{objectiveScoreLabel}</div></Col>
                            <Col span={14}><div style={{ padding: '4px 0', fontSize: 13, fontWeight: 500, textAlign: 'center', background: 'var(--neutral-grey2)' }}>Comment</div></Col>
                          </Row>
                        </Col>
                        <Col span={isPrc ? 6 : 8}>
                          <div style={{ padding: '6px 0', fontWeight: 600, textAlign: 'center', background: 'var(--neutral-grey2)', fontSize: 14 }}>
                            {isFunctionalReview ? 'Indirect Manager Feedback' : 'Feedback'} by {feedbackAuthorName}
                          </div>
                          <Row gutter={4}>
                            <Col span={10}><div style={{ padding: '4px 0', fontSize: 13, fontWeight: 500, textAlign: 'center', background: 'var(--neutral-grey2)' }}>{objectiveScoreLabel}</div></Col>
                            <Col span={14}><div style={{ padding: '4px 0', fontSize: 13, fontWeight: 500, textAlign: 'center', background: 'var(--neutral-grey2)' }}>Comment</div></Col>
                          </Row>
                        </Col>
                      </Row>
                    </div>

                    {/* Rows */}
                    {objectives.map((obj, idx) => (
                      <div key={idx} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: idx < objectives.length - 1 ? '1px solid var(--neutral-grey2)' : 'none' }}>
                        <Row gutter={8} align='top'>
                          <Col span={2}>
                            {usesPriorityOptions
                              ? (
                                <Select style={{ width: '100%' }} value={obj.priority || undefined} disabled>
                                  {!allowedPriorityValues.has(`${obj.priority}`) && obj.priority !== '' && obj.priority !== null && obj.priority !== undefined && (
                                    <Option value={obj.priority}>{obj.priority}% (legacy)</Option>
                                  )}
                                  {objectivePriorityOptions.map(option => <Option key={option.value} value={option.value}>{option.label || option.value}</Option>)}
                                </Select>
                              )
                              : (
                                <Input
                                  type='number'
                                  min={0}
                                  max={100}
                                  step={1}
                                  value={obj.priority}
                                  disabled
                                  placeholder='%'
                                  suffix='%'
                                />
                              )}
                          </Col>
                          <Col span={isPrc ? 5 : 6}>
                            <div style={{ padding: '6px', background: 'var(--neutral-grey0)', minHeight: 60, borderRadius: 4, fontSize: 14 }}>
                              <Text style={{ fontSize: 14 }}>{obj.objective}</Text>
                            </div>
                          </Col>
                          {isPrc && (
                            <Col span={5}>
                              <div style={{ padding: '6px', background: 'var(--neutral-grey0)', minHeight: 60, borderRadius: 4, fontSize: 12 }}>
                                <Space direction='vertical' size={4} style={{ width: '100%' }}>
                                  {prcSuccessIndicatorLevels.map(level => {
                                    const value = getPrcSuccessIndicatorValue(obj, level.key)
                                    return (
                                      <div key={level.key}>
                                        <Text strong style={{ display: 'block', fontSize: 11 }}>{level.label}</Text>
                                        <Text type='secondary' style={{ fontSize: 11, whiteSpace: 'pre-wrap' }}>{value || 'Not provided'}</Text>
                                      </div>
                                    )
                                  })}
                                </Space>
                              </div>
                            </Col>
                          )}
                          <Col span={isPrc ? 6 : 8}>
                            <Row gutter={4}>
                              <Col span={10}>
                                <div style={{ padding: '6px', background: 'var(--neutral-grey0)', minHeight: 32, borderRadius: 4, textAlign: 'center', fontSize: 13 }}>
                                  {(usesConfiguredObjectiveRatings ? getConfiguredRatingDisplay(obj.self_score, formConfig) : getScoreDisplay(obj.self_score)) || <Text type='secondary' style={{ fontSize: 11 }}>Not rated</Text>}
                                </div>
                              </Col>
                              <Col span={14}>
                                <div style={{ padding: '6px', background: 'var(--neutral-grey0)', minHeight: 60, borderRadius: 4, fontSize: 14 }}>
                                  <Text type='secondary' style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{obj.self_comment || 'No comment'}</Text>
                                </div>
                              </Col>
                            </Row>
                          </Col>
                          <Col span={isPrc ? 6 : 8}>
                            <Row gutter={4}>
                              <Col span={10}>
                                {usesConfiguredObjectiveRatings
                                  ? (
                                    <Select
                                      style={{ width: '100%' }}
                                      value={obj.manager_score === '' || obj.manager_score === null ? undefined : Number(obj.manager_score)}
                                      onChange={value => updateObjective(idx, 'manager_score', value)}
                                      disabled={isReadOnly}
                                      placeholder='Rating'
                                      allowClear
                                      status={showValidationErrors && hasObjectiveText(obj) && (
                                        obj.manager_score === null || obj.manager_score === undefined || obj.manager_score === ''
                                      ) ? 'error' : undefined}
                                    >
                                      {getConfiguredRatingOptions(formConfig, obj.manager_score).map(option => (
                                        <Option key={option.value} value={option.value}>{option.label}</Option>
                                      ))}
                                    </Select>
                                  )
                                  : (
                                    <Input
                                      type='number'
                                      min={1}
                                      max={objectiveScoreMax}
                                      step='0.01'
                                      style={{ width: '100%' }}
                                      value={obj.manager_score}
                                      onChange={e => {
                                        if ((allowDecimalObjectiveScores ? isDecimalInput : isWholeNumberInput)(e.target.value)) {
                                          updateObjective(idx, 'manager_score', e.target.value)
                                        }
                                      }}
                                      onBlur={e => updateObjective(idx, 'manager_score', normalizeObjectiveScore(e.target.value, objectiveScoreMax))}
                                      disabled={isReadOnly}
                                      placeholder='Score'
                                    />
                                  )}
                                {showValidationErrors && hasObjectiveText(obj) && (
                                  obj.manager_score === null || obj.manager_score === undefined || obj.manager_score === ''
                                ) && (
                                    <Text type='danger' style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                                      {feedbackAuthorName}'s rating is required.
                                    </Text>
                                  )}
                              </Col>
                              <Col span={14}>
                                <TextArea
                                  rows={2}
                                  value={obj.manager_comment}
                                  onChange={e => updateObjective(idx, 'manager_comment', e.target.value)}
                                  disabled={isReadOnly}
                                  placeholder='Comment...'
                                  style={{ fontSize: 14 }}
                                />
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </div>
                    ))}

                    {/* Priority total indicator */}
                    {!usesPriorityOptions && (
                      <div style={{ marginTop: 8, marginBottom: 16 }}>
                        <Text type={Math.abs(totalPriority - 100) < 0.01 ? 'success' : 'danger'} strong>
                          Total Priority: {totalPriority}% {Math.abs(totalPriority - 100) < 0.01 ? '✓' : '(must equal 100%)'}
                        </Text>
                      </div>
                    )}

                    {/* Averages */}
                    <div style={{ marginTop: 16, borderTop: '1px solid var(--tti-light-grey)', background: 'var(--tti-blue-light)', padding: '20px 24px' }}>
                      {showObjectiveAverageScore && (
                        <Row gutter={8} align='middle'>
                          <Col span={8}><Text strong style={{ fontSize: 24, color: 'var(--tti-blue)' }}>Average Score</Text></Col>
                          <Col span={8}>
                            <div style={{ textAlign: 'center', padding: 10, background: 'var(--neutral-white)' }}>
                              <Text strong style={{ display: 'block', fontSize: 24, color: 'var(--tti-info)' }}>{objectiveSelfAvg || '--'}</Text>
                              {objectiveSelfAvg && (
                                <Text type='secondary' style={{ display: 'block', fontSize: 14, marginTop: 4 }}>
                                  {isPrc ? prcEvaluationLevel(objectiveSelfAvg) : getRatingCategory(objectiveSelfAvg)?.rating}
                                </Text>
                              )}
                            </div>
                          </Col>
                          <Col span={8}>
                            <div style={{ textAlign: 'center', padding: 10, background: 'var(--neutral-white)' }}>
                              <Text strong style={{ display: 'block', fontSize: 24, color: 'var(--tti-success)' }}>{objectiveManagerAvg || '--'}</Text>
                              {objectiveManagerAvg && (
                                <Text type='secondary' style={{ display: 'block', fontSize: 14, marginTop: 4 }}>
                                  {isPrc ? prcEvaluationLevel(objectiveManagerAvg) : getRatingCategory(objectiveManagerAvg)?.rating}
                                </Text>
                              )}
                            </div>
                          </Col>
                        </Row>
                      )}
                      {objectiveSummaryEnabled && (
                        <>
                          <SuggestedRatingBand items={[
                            { label: 'Employee Calculated Suggested Rating', value: employeeObjectiveSuggestedRating },
                            { label: 'Manager Calculated Suggested Rating', value: managerObjectiveSuggestedRating }
                          ]} />
                          <Row gutter={8} align='middle' style={{ marginTop: 12 }}>
                            <Col span={8}><Text strong>Overall Rating of Objective Appraisal (manual)</Text></Col>
                            <Col span={8}>
                              <div style={{ textAlign: 'center', padding: 10, background: 'var(--neutral-white)' }}>
                                <Text strong>
                                  {getConfiguredRatingFullLabel(
                                    getConfiguredRatingOptions(formConfig).find(
                                      option => option.value === Number(formData[employeeObjectiveRatingField])
                                    )
                                  ) || '--'}
                                </Text>
                              </div>
                            </Col>
                            <Col span={8}>
                              <Select
                                size='large'
                                style={{ width: '100%' }}
                                value={formData[managerObjectiveRatingField] === null || formData[managerObjectiveRatingField] === undefined
                                  ? undefined
                                  : Number(formData[managerObjectiveRatingField])}
                                onChange={value => setFormData(prev => ({ ...prev, [managerObjectiveRatingField]: value }))}
                                disabled={isReadOnly}
                                placeholder='Select rating'
                                allowClear
                                status={showValidationErrors &&
                                  appraisal?.session_template === 'vietnam_milwaukee' &&
                                  objectiveSummaryConfig.required &&
                                  !formData[managerObjectiveRatingField]
                                  ? 'error'
                                  : undefined}
                              >
                                {getConfiguredRatingOptions(formConfig, formData[managerObjectiveRatingField]).map(option => (
                                  <Option key={option.value} value={option.value}>{getConfiguredRatingFullLabel(option)}</Option>
                                ))}
                              </Select>
                            </Col>
                          </Row>
                        </>
                      )}
                    </div>
                  </>
                )
                : (
                  <Text type='secondary'>No annual objectives were submitted for this appraisal.</Text>
                )}
            </Card>

            {isCheckboxFlow
              ? (
                <PerformanceFactorsCard
                  formStructure={formStructure}
                  formConfig={formConfig}
                  getFieldValue={getFieldValue}
                  updateField={updateField}
                  getQuestionRatingOptions={getQuestionRatingOptions}
                  getRatingOptionLabel={getQuestionRatingLabel}
                  getRatingValueDisplay={value => usesConfiguredRatings ? getConfiguredRatingDisplay(value, formConfig) : getRatingValueDisplay(value, ratingGuideSource)}
                  editableSide={isReadOnly ? null : 'manager'}
                  managerSubmitted={displayedManagerFeedbackAvailable}
                  showValidationErrors={showValidationErrors}
                />
              )
              : formStructure.map((category, catIdx) => (
                <Card
                  key={category.id}
                  className='tti-appraisal-section-card'
                  style={{ marginBottom: 24 }}
                  title={(
                    <div>
                      <Text strong style={{ fontSize: 16 }}>{category.title}</Text>
                      {category.description && (
                        <Text type='secondary' style={{ display: 'block', marginTop: 4, whiteSpace: 'normal' }}>
                          {category.description}
                        </Text>
                      )}
                    </div>
                  )}
                >
                  {/* Two-tiered header */}
                  <div style={{ marginBottom: 16, borderBottom: '2px solid #d9d9d9' }}>
                    <Row gutter={16}>
                      <Col span={6}>
                        <div style={{ padding: '8px 0', fontWeight: 600, textAlign: 'center', background: 'var(--neutral-grey0)', fontSize: 14 }}>
                          Question
                        </div>
                      </Col>
                      <Col span={9}>
                        <div style={{ padding: '8px 0', fontWeight: 600, textAlign: 'center', background: 'var(--bu-primary-bg)', borderLeft: '2px solid var(--bu-primary-border)', borderRight: '2px solid var(--bu-primary-border)', fontSize: 14 }}>
                          {isPrc ? `Self-Evaluation ${objectiveScoreLabel}` : 'Self-Evaluation'}
                        </div>
                      </Col>
                      <Col span={9}>
                        <div style={{ padding: '8px 0', fontWeight: 600, textAlign: 'center', background: 'var(--bu-secondary-bg)', borderLeft: '2px solid var(--bu-secondary-border)', borderRight: '2px solid var(--bu-secondary-border)', fontSize: 14 }}>
                          Evaluation by {feedbackAuthorName}
                        </div>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={6} />
                      <Col span={9}>
                        <Row gutter={8}>
                          <Col span={singleCategoryComment ? 24 : 10}>
                            <div style={{ padding: '4px 0', fontSize: 14, fontWeight: 500, textAlign: 'center', background: 'var(--bu-primary-bg)' }}>
                              Rating
                            </div>
                          </Col>
                          {!singleCategoryComment && (
                            <Col span={14}>
                              <div style={{ padding: '4px 0', fontSize: 14, fontWeight: 500, textAlign: 'center', background: 'var(--bu-primary-bg)' }}>
                                Comments
                              </div>
                            </Col>
                          )}
                        </Row>
                      </Col>
                      <Col span={9}>
                        <Row gutter={8}>
                          <Col span={singleCategoryComment ? 24 : 10}>
                            <div style={{ padding: '4px 0', fontSize: 14, fontWeight: 500, textAlign: 'center', background: 'var(--bu-secondary-bg)' }}>
                              Rating
                            </div>
                          </Col>
                          {!singleCategoryComment && (
                            <Col span={14}>
                              <div style={{ padding: '4px 0', fontSize: 14, fontWeight: 500, textAlign: 'center', background: 'var(--bu-secondary-bg)' }}>
                                Comments
                              </div>
                            </Col>
                          )}
                        </Row>
                      </Col>
                    </Row>
                  </div>

                  {/* Questions */}
                  {category.questions.map((question, qIdx) => (
                    <div key={question.id} style={{ marginBottom: 16, paddingBottom: 16, borderBottom: qIdx < category.questions.length - 1 ? '1px solid var(--neutral-grey2)' : 'none' }}>
                      <Row gutter={16} align='top'>
                        <Col span={6}>
                          <Text style={{ fontSize: 15, lineHeight: '1.5' }}>
                            {qIdx + 1}. {question.text}
                          </Text>
                        </Col>
                        <Col span={9}>
                          <Row gutter={8}>
                            <Col span={singleCategoryComment ? 24 : 10}>
                              <div style={{ padding: '8px', background: 'var(--neutral-grey0)', minHeight: 60, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {getFieldValue(category.id, question.id, 'employee_rating')
                                  ? (
                                    <Text style={{ fontSize: 14 }}>
                                      {isPrc
                                        ? `${getFieldValue(category.id, question.id, 'employee_rating')} - ${prcEvaluationLevel(getFieldValue(category.id, question.id, 'employee_rating'))}`
                                        : (usesConfiguredRatings
                                          ? getConfiguredRatingDisplay(getFieldValue(category.id, question.id, 'employee_rating'), formConfig)
                                          : getRatingValueDisplay(getFieldValue(category.id, question.id, 'employee_rating'), ratingGuideSource))}
                                    </Text>
                                  )
                                  : (
                                    <Text type='secondary' style={{ fontSize: 14 }}>Not rated</Text>
                                  )}
                              </div>
                            </Col>
                            {!singleCategoryComment && (
                              <Col span={14}>
                                <div style={{ padding: 8, background: 'var(--neutral-grey0)', minHeight: 60, borderRadius: 4 }}>
                                  <Text type='secondary' style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>
                                    {getFieldValue(category.id, question.id, 'employee_comments') || 'No comments'}
                                  </Text>
                                </div>
                              </Col>
                            )}
                          </Row>
                        </Col>
                        <Col span={9}>
                          <Row gutter={8}>
                            <Col span={singleCategoryComment ? 24 : 10}>
                              {isPrc
                                ? (
                                  <Input
                                    type='number'
                                    min={1}
                                    max={10}
                                    step={1}
                                    value={getFieldValue(category.id, question.id, 'manager_rating')}
                                    onChange={(e) => {
                                      if (isWholeNumberInput(e.target.value)) updateField(category.id, question.id, 'manager_rating', e.target.value)
                                    }}
                                    onBlur={e => updateField(category.id, question.id, 'manager_rating', normalizeObjectiveScore(e.target.value, 10))}
                                    disabled={isReadOnly}
                                    placeholder='Score'
                                    status={showValidationErrors && !getFieldValue(category.id, question.id, 'manager_rating') ? 'error' : undefined}
                                  />
                                )
                                : (
                                  <Select
                                    style={{ width: '100%' }}
                                    value={getFieldValue(category.id, question.id, 'manager_rating')}
                                    onChange={(v) => updateField(category.id, question.id, 'manager_rating', v)}
                                    disabled={isReadOnly}
                                    placeholder='Select rating...'
                                    allowClear
                                    optionLabelProp='label'
                                    status={showValidationErrors && !getFieldValue(category.id, question.id, 'manager_rating') ? 'error' : undefined}
                                  >
                                    {getQuestionRatingOptions(getFieldValue(category.id, question.id, 'manager_rating')).map(opt => (
                                      <Option key={opt.value} value={opt.value} label={getQuestionRatingLabel(opt)}>
                                        {getQuestionRatingLabel(opt)}
                                      </Option>
                                    ))}
                                  </Select>
                                )}
                              {showValidationErrors && !getFieldValue(category.id, question.id, 'manager_rating') && (
                                <Text type='danger' style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                                  Rating is required.
                                </Text>
                              )}
                            </Col>
                            {!singleCategoryComment && (
                              <Col span={14}>
                                <TextArea
                                  rows={2}
                                  value={getFieldValue(category.id, question.id, 'manager_comments')}
                                  onChange={(e) => updateField(category.id, question.id, 'manager_comments', e.target.value)}
                                  disabled={isReadOnly}
                                  placeholder='Comments...'
                                  style={{ fontSize: 14 }}
                                />
                              </Col>
                            )}
                          </Row>
                        </Col>
                      </Row>
                    </div>
                  ))}

                  {singleCategoryComment && category.questions.some(question => question.id === categoryCommentQuestionKey) && (
                    <Row gutter={16} style={{ marginTop: 16 }}>
                      <Col span={6}>
                        <Text strong>{categoryCommentLabel}{categoryCommentRequired ? ' *' : ''}</Text>
                      </Col>
                      <Col span={9}>
                        <div style={{ padding: 8, background: 'var(--neutral-grey0)', minHeight: 72, borderRadius: 4 }}>
                          <Text type='secondary' style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>
                            {getFieldValue(category.id, categoryCommentQuestionKey, 'employee_comments') || 'No comment'}
                          </Text>
                        </div>
                      </Col>
                      <Col span={9}>
                        <TextArea
                          rows={3}
                          value={getFieldValue(category.id, categoryCommentQuestionKey, 'manager_comments')}
                          onChange={(e) => updateField(category.id, categoryCommentQuestionKey, 'manager_comments', e.target.value)}
                          disabled={isReadOnly}
                          placeholder={`${categoryCommentLabel}...`}
                          status={showValidationErrors && categoryCommentRequired && !String(getFieldValue(category.id, categoryCommentQuestionKey, 'manager_comments') || '').trim() ? 'error' : undefined}
                          style={{ fontSize: 14 }}
                        />
                        {showValidationErrors && categoryCommentRequired && !String(getFieldValue(category.id, categoryCommentQuestionKey, 'manager_comments') || '').trim() && (
                          <Text type='danger' style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                            {categoryCommentLabel} is required.
                          </Text>
                        )}
                      </Col>
                    </Row>
                  )}

                  {/* Category Averages */}
                  <CalculatedRatingBand label={categoryAverageLabel} items={[
                    {
                      label: 'Employee Average',
                      value: showCategoryAverageAsRating
                        ? calculateCategorySuggestedRating(category.id, 'employee_rating')
                        : calculateCategoryAverage(category.id, 'employee_rating'),
                      detail: !showCategoryAverageAsRating && calculateCategoryAverage(category.id, 'employee_rating')
                        ? (isPrc
                          ? prcEvaluationLevel(calculateCategoryAverage(category.id, 'employee_rating'))
                          : getRatingCategory(calculateCategoryAverage(category.id, 'employee_rating'))?.rating)
                        : null
                    },
                    {
                      label: `Average Rating by ${feedbackAuthorName}`,
                      value: showCategoryAverageAsRating
                        ? calculateCategorySuggestedRating(category.id, 'manager_rating')
                        : calculateCategoryAverage(category.id, 'manager_rating'),
                      detail: !showCategoryAverageAsRating && calculateCategoryAverage(category.id, 'manager_rating')
                        ? (isPrc
                          ? prcEvaluationLevel(calculateCategoryAverage(category.id, 'manager_rating'))
                          : getRatingCategory(calculateCategoryAverage(category.id, 'manager_rating'))?.rating)
                        : null
                    }
                  ]} />
                </Card>
              ))}

            <Card
              className='tti-appraisal-section-card'
              style={{ marginBottom: 24 }}
              title={<Text strong style={{ fontSize: 16 }}>{competencySummaryTitle}{competencySummarySubtitle && <><br />({competencySummarySubtitle})</>}</Text>}
            >
              {showCompetencySummarySubtext && (
                <Text type='secondary' style={{ display: 'block', marginBottom: 12 }}>
                  Calculated as the average of all competency category ratings.
                </Text>
              )}
              <SuggestedRatingBand items={[
                {
                  label: 'Employee Calculated Suggested Rating',
                  value: showCategoryAverageAsRating ? employeeCompetencySuggestedRating : overallEmployeeCategoryAvg,
                  detail: !showCategoryAverageAsRating && overallEmployeeCategoryAvg
                    ? (isPrc ? prcEvaluationLevel(overallEmployeeCategoryAvg) : getRatingCategoryDisplay(overallEmployeeCategoryAvg))
                    : null
                },
                {
                  label: 'Manager Calculated Suggested Rating',
                  value: showCategoryAverageAsRating ? managerCompetencySuggestedRating : overallManagerCategoryAvg,
                  detail: !showCategoryAverageAsRating && overallManagerCategoryAvg
                    ? (isPrc ? prcEvaluationLevel(overallManagerCategoryAvg) : getRatingCategoryDisplay(overallManagerCategoryAvg))
                    : null
                }
              ]} />
              {competencyManualRating.enabled && (
                <Row gutter={16} style={{ marginTop: 12 }}>
                  <Col span={12}>
                    <div style={{ textAlign: 'center', padding: 10, background: 'var(--bu-primary-bg)', borderRadius: 4 }}>
                      <Text strong style={{ display: 'block', marginBottom: 6 }}>{competencySummaryTitle} (Employee manual)</Text>
                      <Text strong>{getConfiguredRatingFullLabel(
                        getConfiguredRatingOptions(formConfig).find(
                          option => option.value === Number(formData[employeeCompetencyRatingField])
                        )
                      ) || '--'}</Text>
                    </div>
                  </Col>
                  <Col span={12}>
                    <Text strong style={{ display: 'block', marginBottom: 6 }}>{competencySummaryTitle} (Manager manual)</Text>
                    <Select
                      size='large'
                      style={{ width: '100%' }}
                      value={formData[managerCompetencyRatingField] == null ? undefined : Number(formData[managerCompetencyRatingField])}
                      onChange={value => setFormData(prev => ({ ...prev, [managerCompetencyRatingField]: value }))}
                      disabled={isReadOnly}
                      placeholder={`${competencySummaryTitle} (manual)`}
                      status={showValidationErrors &&
                        appraisal?.session_template === 'vietnam_milwaukee' &&
                        competencyManualRating.required &&
                        !formData[managerCompetencyRatingField]
                        ? 'error'
                        : undefined}
                    >
                      {getConfiguredRatingOptions(formConfig, formData[managerCompetencyRatingField]).map(option => (
                        <Option key={option.value} value={option.value}>{getConfiguredRatingFullLabel(option)}</Option>
                      ))}
                    </Select>
                  </Col>
                </Row>
              )}
            </Card>

            <Card
              className='tti-appraisal-section-card'
              style={{ marginBottom: 24 }}
              title={<Text style={{ color: '#FFF' }}>{overallPerformanceTitle}</Text>}
            >
              {showOverallSummarySubtext && (
                <Text type='secondary' style={{ display: 'block', marginBottom: 12 }}>
                  Calculated equally from the weighted Objectives score and Competency score.
                </Text>
              )}
              <SuggestedRatingBand items={[
                {
                  label: 'Employee Calculated Suggested Rating',
                  value: showCategoryAverageAsRating ? employeeOverallSuggestedRating : overallEmployeeAverage,
                  detail: !showCategoryAverageAsRating && overallEmployeeAverage ? getRatingCategoryDisplay(overallEmployeeAverage) : null
                },
                {
                  label: 'Manager Calculated Suggested Rating',
                  value: showCategoryAverageAsRating ? managerOverallSuggestedRating : overallManagerAverage,
                  detail: !showCategoryAverageAsRating && overallManagerAverage ? getRatingCategoryDisplay(overallManagerAverage) : null
                }
              ]} />
              {overallManualRating.enabled && (
                <Row gutter={16} style={{ marginTop: 12 }}>
                  <Col span={12}>
                    <div style={{ textAlign: 'center', padding: 10, background: 'var(--bu-primary-bg)', borderRadius: 4 }}>
                      <Text strong style={{ display: 'block', marginBottom: 6 }}>{overallPerformanceTitle} (Employee manual)</Text>
                      <Text strong>{getConfiguredRatingFullLabel(
                        getConfiguredRatingOptions(formConfig).find(
                          option => option.value === Number(formData[employeeOverallRatingField])
                        )
                      ) || '--'}</Text>
                    </div>
                  </Col>
                  <Col span={12}>
                    <Text
                      strong
                      type={isManagerOverallPerformanceRatingMissing ? 'danger' : undefined}
                      style={{ display: 'block', marginBottom: 6 }}
                    >
                      {overallPerformanceTitle} (Manager manual)
                    </Text>
                    <Select
                      size='large'
                      style={{ width: '100%' }}
                      value={formData[managerOverallRatingField] == null ? undefined : Number(formData[managerOverallRatingField])}
                      onChange={value => setFormData(prev => ({ ...prev, [managerOverallRatingField]: value }))}
                      disabled={isReadOnly}
                      placeholder={`${overallPerformanceTitle} (manual)`}
                      status={isManagerOverallPerformanceRatingMissing ? 'error' : undefined}
                    >
                      {getConfiguredRatingOptions(formConfig, formData[managerOverallRatingField]).map(option => (
                        <Option key={option.value} value={option.value}>{getConfiguredRatingFullLabel(option)}</Option>
                      ))}
                    </Select>
                    {isManagerOverallPerformanceRatingMissing && (
                      <Text type='danger' style={{ display: 'block', marginTop: 4 }}>
                        {overallPerformanceTitle} is required.
                      </Text>
                    )}
                    {appraisal?.session_template === 'vietnam_milwaukee' && requiresOverallRatingJustification && (
                      <div style={{ marginTop: 12 }}>
                        <Text strong style={{ display: 'block', marginBottom: 6 }}>
                          Rating Justification *
                        </Text>
                        <TextArea
                          rows={3}
                          value={formData.manager_overall_rating_justification || ''}
                          onChange={event => setFormData(prev => ({ ...prev, manager_overall_rating_justification: event.target.value }))}
                          disabled={isReadOnly}
                          placeholder={`Required — minimum ${justificationMinLength} characters`}
                          status={showValidationErrors && requiresOverallRatingJustification &&
                            (formData.manager_overall_rating_justification || '').trim().length < justificationMinLength
                            ? 'error'
                            : undefined}
                          showCount
                        />
                      </div>
                    )}
                  </Col>
                </Row>
              )}
            </Card>

            {isCheckboxFlow && (
              <CultureValuesCheckboxes
                formData={formData}
                formConfig={formConfig}
                disabled
              />
            )}

            {isCheckboxFlow && (
              <NextYearObjectivesCard
                formData={formData}
                formConfig={formConfig}
                disabled={isReadOnly}
                onChange={nextYearObjectives => setFormData(prev => ({ ...prev, next_year_objectives: nextYearObjectives }))}
              />
            )}

            {isPrc && (
              <Card className='tti-appraisal-section-card' title={<Text style={{ color: '#FFF', fontSize: 16 }}>Individual Development Plan (IDP)</Text>} style={{ marginBottom: 24 }}>
                <Row gutter={[16, 16]}>
                  {prcIdpFieldRows.map(([field, label]) => {
                    const managerOwned = field === 'supervisor_year_end_suggestion'
                    return (
                      <Col span={12} key={field}>
                        <Text strong>{label}</Text>
                        <TextArea
                          rows={4}
                          value={prcIdp[field] || ''}
                          onChange={e => updatePrcIdp(field, e.target.value)}
                          disabled={isReadOnly || !managerOwned}
                          placeholder={managerOwned ? label : 'Employee field'}
                          style={{ marginTop: 8 }}
                        />
                      </Col>
                    )
                  })}
                </Row>
              </Card>
            )}

            {/* Overall Comments Section */}
            <Card className='tti-appraisal-section-card' title={<Text style={{ color: '#FFF' }}>Overall Comments</Text>} style={{ marginBottom: 24 }}>
              <Row gutter={16}>
                {appraisal.employee_overall_comment?.trim() && (
                  <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                      <Text strong>Employee Overall Comment</Text>
                    </div>
                    <div style={{ padding: 12, background: 'var(--neutral-grey0)', minHeight: 100, borderRadius: 4 }}>
                      <Text type='secondary' style={{ whiteSpace: 'pre-wrap' }}>
                        {appraisal.employee_overall_comment}
                      </Text>
                    </div>
                  </Col>
                )}
                <Col span={appraisal.employee_overall_comment?.trim() ? 12 : 24}>
                  <div style={{ marginBottom: 8 }}>
                    <Text strong>Overall Comment by {feedbackAuthorName}</Text>
                  </div>
                  <TextArea
                    rows={4}
                    value={managerOverallComment}
                    onChange={(e) => setManagerOverallComment(e.target.value)}
                    status={showValidationErrors && !managerOverallComment.trim() ? 'error' : undefined}
                    disabled={isReadOnly}
                    placeholder='Please provide your overall comments...'
                  />
                </Col>
              </Row>
            </Card>

            {/* Action Buttons */}
            {stickyActions}

            {isReadOnly && (
              <Alert
                message={assignedToAnotherApprover
                  ? `This review is assigned to ${firstApproverName}`
                  : awaitingEmployee
                    ? 'Waiting for the employee to submit their appraisal'
                    : 'This review has already been submitted'}
                description={assignedToAnotherApprover
                  ? 'You can view this appraisal but cannot make changes or submit it.'
                  : awaitingEmployee
                    ? 'You can start your review once the employee has submitted their part.'
                    : 'You can view your responses but cannot make changes.'}
                type='info'
                showIcon
                style={{ marginTop: 24 }}
              />
            )}
          </>
        )}
      </div>
      {conversationSidebar}
    </div>
  )
}
